import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PLANT_DATA_REVIEW_QUERY_KEY } from './IndustrialCostFileReviewPanel';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  getIndustrialCostDirectMaterials,
  getIndustrialCostProducts,
  getLatestDailyLoadRun,
  getPlantDataReview,
  saveDailyLoadRun,
} from '../services/industrialCostService';
import type {
  DailyLoadRun,
  DailyLoadRunFile,
  IndustrialCostDataSourceStatusItem,
} from '../services/industrialCostService';
import {
  IMPORT_PERMISSION_DENIED_TEXT,
  useCanManageImports,
} from '../utils/importPermission';
import {
  DAILY_STATUS_COLORS,
  DAILY_STATUS_LABELS,
  buildDailyRunFile,
  dailyRunMessage,
  missingDailySources,
  resultLine,
} from '../utils/dailyLoadRunFile';
import {
  applyDirectInputsImportMapping,
  applyDirectInputsMaterialResolution,
  confirmDirectInputsImport,
  createDirectInputsImportFlowState,
  getDirectInputsImportSummary,
  getDirectInputsUnresolvedMaterialRefs,
  runDirectInputsImportPreview,
} from '../importFlows/directInputsImportFlow';
import {
  applyProductionImportConfig,
  applyProductionImportMapping,
  applyProductionProductResolution,
  canContinueProductionImport,
  confirmProductionImport,
  productionShrinkWarning,
  createProductionImportFlowState,
  getProductionUnresolvedProductRefs,
  runProductionImportPreview,
} from '../importFlows/productionImportFlow';
import {
  applyImportedManufacturingOrdersImportMapping,
  applyImportedManufacturingOrdersProductResolution,
  confirmImportedManufacturingOrdersImport,
  createImportedManufacturingOrdersImportFlowState,
  getImportedManufacturingOrdersImportSummary,
  getImportedManufacturingOrdersUnresolvedProductRefs,
  runImportedManufacturingOrdersImportPreview,
} from '../importFlows/importedManufacturingOrdersImportFlow';
import type {
  DirectMaterial,
  DirectMaterialEntryImportPreviewResponse,
  ImportedManufacturingOrderImportPreviewResponse,
  Product,
  ProductionImportPreview,
  PurchaseOrderImportPreviewResponse,
  ProductionUploadMode,
  SourceGranularity,
} from '../types/industrialCost';
import type { DirectInputsImportFlowState } from '../importFlows/directInputsImportFlow';
import type { ImportedManufacturingOrdersImportFlowState } from '../importFlows/importedManufacturingOrdersImportFlow';
import type { ProductionImportFlowState } from '../importFlows/productionImportFlow';
import {
  confirmPurchaseOrdersImport,
  createPurchaseOrdersImportFlowState,
  getPurchaseOrdersImportSummary,
  runPurchaseOrdersImportPreview,
} from '../importFlows/purchaseOrdersImportFlow';
import type { PurchaseOrdersImportFlowState } from '../importFlows/purchaseOrdersImportFlow';
import {
  analyzeFileNormalization,
  commitFileNormalization,
} from '../fileNormalization/service';
import {
  evaluateImportCenterDetectionFromFilename,
  getImportCenterDetectedTypeChipColor,
  getImportCenterDetectedTypeLabel,
  getImportCenterOrderedCandidateKeys,
  getImportCenterSelectableDetectedTypes,
  getImportCenterVisibleImporters,
  isAcceptedImportCenterFile,
} from '../importCenter/importerDefinitions';
import type {
  ImportCenterDetectedType,
  ImportCenterDetectionResult,
} from '../importCenter/importerDefinitions';

type ImportCenterPreviewStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'needs_mapping'
  | 'needs_resolution'
  | 'needs_review'
  | 'error'
  | 'skipped'
  | 'importing'
  | 'imported'
  | 'imported_with_issues';

type ImportCenterProductionConfig = {
  sourceGranularity: SourceGranularity;
  declaredPeriodYear: string;
  declaredPeriodMonth: string;
  uploadMode: ProductionUploadMode;
};

type ImportCenterFileItem = {
  id: string;
  file: File;
  relativePath: string;
  detection: ImportCenterDetectionResult;
  detectedType: ImportCenterDetectedType;
  autoReassignedByDetection: boolean;
  autoSelectedBestCandidate: boolean;
  learnedSuggestedImporter:
    | Exclude<ImportCenterDetectedType, 'unknown' | 'excluded'>
    | null;
  learnedSuggestionScore: number | null;
  learnedSuggestionSource: 'intra-lot' | 'persisted' | null;
  lockedDailyImporter: boolean;
  previewStatus: ImportCenterPreviewStatus;
  previewError: string | null;
  missingRequiredFields: string[];
  detectedColumns: string[];
  previewRows: Array<Record<string, unknown>>;
  mapping: Record<string, string | null>;
  productResolution: Record<string, string>;
  materialResolution: Record<string, string | null>;
  productionConfig: ImportCenterProductionConfig;
  productionPreview: ProductionImportPreview | null;
  productionFlow: ProductionImportFlowState | null;
  directInputsPreview: DirectMaterialEntryImportPreviewResponse | null;
  directInputsFlow: DirectInputsImportFlowState | null;
  importedManufacturingOrdersPreview: ImportedManufacturingOrderImportPreviewResponse | null;
  importedManufacturingOrdersFlow: ImportedManufacturingOrdersImportFlowState | null;
  purchaseOrdersPreview: PurchaseOrderImportPreviewResponse | null;
  purchaseOrdersFlow: PurchaseOrdersImportFlowState | null;
  confirmSummary: string | null;
};

const today = new Date();
const defaultProductionConfig: ImportCenterProductionConfig = {
  sourceGranularity: 'day',
  declaredPeriodYear: String(today.getFullYear()),
  declaredPeriodMonth: String(today.getMonth() + 1),
  uploadMode: 'replace_by_date_range',
};

const fieldLabels: Record<string, string> = {
  product: 'Producto',
  product_ref: 'Producto',
  PRODUCTO: 'Producto',
  material: 'Material',
  material_ref: 'Material',
  entry_date: 'Fecha entrada',
  date: 'Fecha',
  production_date: 'Fecha producción',
  FECHA: 'Fecha producción',
  quantity: 'Cantidad',
  unit_price: 'Precio unitario',
  kg: 'Kg',
  kg_produced: 'Kg',
  KG: 'Kg',
  units: 'Unidades',
  units_produced: 'Unidades',
  UNIDADES: 'Cantidad fabricada',
  UNIDADES_ADICIONALES: 'Cantidad fabricada adicional',
  REFERENCIA: 'Referencia origen',
  CLIENTE: 'Cliente',
  OF: 'Orden de fabricación',
  supplier: 'Proveedor',
  supplier_name: 'Nombre del proveedor',
  document_ref: 'Documento',
  reference: 'Referencia',
  source_reference: 'Referencia origen',
  notes: 'Notas',
  is_active: 'Activo',
  period_year: 'Año',
  period_month: 'Mes',
  purchase_order_number: 'Pedido de compra',
  material_code: 'Artículo / material',
  quantity_pending: 'Cantidad pendiente',
  expected_delivery_date: 'Fecha prevista de entrega',
};

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeResponse.response?.data?.detail) {
      return maybeResponse.response.data.detail;
    }

    if (maybeResponse.message) {
      return maybeResponse.message;
    }
  }

  return fallback;
}

function getDetectedTypeLabel(type: ImportCenterDetectedType): string {
  return getImportCenterDetectedTypeLabel(type);
}

function getDetectedTypeChipColor(
  type: ImportCenterDetectedType,
): 'success' | 'primary' | 'default' | 'warning' {
  return getImportCenterDetectedTypeChipColor(type);
}

function getPreviewStatusLabel(status: ImportCenterPreviewStatus): string {
  if (status === 'loading') return 'Analizando';
  if (status === 'ready') return 'Listo para importar';
  if (status === 'needs_mapping') return 'Requiere mapeo';
  if (status === 'needs_resolution') return 'Requiere homologación';
  if (status === 'needs_review') return 'Requiere revisión de filas';
  if (status === 'error') return 'Error';
  if (status === 'skipped') return 'Fuera del lote';
  if (status === 'importing') return 'Importando';
  if (status === 'imported') return 'Importado';
  if (status === 'imported_with_issues') return 'Importado con incidencias';
  return 'Pendiente';
}

function getPreviewStatusChipColor(
  status: ImportCenterPreviewStatus,
): 'success' | 'warning' | 'error' | 'default' | 'primary' {
  if (status === 'ready' || status === 'imported') return 'success';
  if (status === 'needs_mapping' || status === 'needs_resolution' || status === 'needs_review' || status === 'imported_with_issues') {
    return 'warning';
  }
  if (status === 'error') return 'error';
  if (status === 'loading' || status === 'importing') return 'primary';
  return 'default';
}

function getDetectionConfidenceLabel(
  confidence: ImportCenterDetectionResult['confidence'],
): string {
  if (confidence === 'high') return 'Confianza alta';
  if (confidence === 'medium') return 'Confianza media';
  if (confidence === 'low') return 'Confianza baja';
  return 'Sin confianza';
}

function getDetectionConfidenceChipColor(
  confidence: ImportCenterDetectionResult['confidence'],
): 'success' | 'warning' | 'default' {
  if (confidence === 'high') return 'success';
  if (confidence === 'medium' || confidence === 'low') return 'warning';
  return 'default';
}

function formatFieldLabel(field: string): string {
  return (
    fieldLabels[field] ??
    field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (match) => match.toUpperCase())
  );
}

function formatPreviewValue(value: unknown): string {
  if (value === null || value === undefined) return '—';

  if (typeof value === 'number' && Number.isNaN(value)) {
    return '—';
  }

  const text = String(value).trim();

  if (!text || ['nan', 'none', 'null', 'undefined'].includes(text.toLowerCase())) {
    return '—';
  }

  return text;
}

function buildBaseFileItem(file: File, index: number): ImportCenterFileItem {
  const relativePath =
    (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
  const persistedSuggestion = getPersistedLearnedSuggestionForItem({
    relativePath,
    detectedColumns: [],
    mapping: {},
  });

  const filenameDetection =
    evaluateImportCenterDetectionFromFilename(relativePath);

  const hasStrongPersistedSuggestion =
    Boolean(persistedSuggestion) &&
    (persistedSuggestion?.score ?? 0) >=
      IMPORT_CENTER_PERSISTED_SUGGESTION_STRONG_SCORE;

  const detectedType: ImportCenterDetectedType =
    filenameDetection.detectedType === 'unknown' &&
    hasStrongPersistedSuggestion &&
    persistedSuggestion
      ? persistedSuggestion.importer
      : filenameDetection.detectedType;

  const detection: ImportCenterDetectionResult =
    detectedType !== filenameDetection.detectedType &&
    persistedSuggestion
      ? {
          ...filenameDetection,
          detectedType,
          confidence: 'medium',
          decisionReason:
            `${filenameDetection.decisionReason} ` +
            `Se aplica provisionalmente la sugerencia aprendida ` +
            `${getDetectedTypeLabel(persistedSuggestion.importer)} ` +
            `(score ${persistedSuggestion.score}) y se validará contra ` +
            `la estructura real del archivo.`,
        }
      : persistedSuggestion
        ? {
            ...filenameDetection,
            decisionReason:
              `${filenameDetection.decisionReason} ` +
              `Existe además una sugerencia recordada de importador ` +
              `para archivos similares.`,
          }
        : filenameDetection;

  return {
    id: `${relativePath}-${index}`,
    file,
    relativePath,
    detection,
    detectedType,
    autoReassignedByDetection: false,
    autoSelectedBestCandidate: false,
    learnedSuggestedImporter: persistedSuggestion?.importer ?? null,
    learnedSuggestionScore: persistedSuggestion?.score ?? null,
    learnedSuggestionSource: persistedSuggestion ? 'persisted' : null,
    lockedDailyImporter: false,
    previewStatus:
      detectedType === 'excluded' ? 'skipped' : 'idle',
    previewError: null,
    missingRequiredFields: [],
    detectedColumns: [],
    previewRows: [],
    mapping: {},
    productResolution: {},
    materialResolution: {},
    productionConfig: { ...defaultProductionConfig },
    productionPreview: null,
    productionFlow: null,
    directInputsPreview: null,
    directInputsFlow: null,
    importedManufacturingOrdersPreview: null,
    importedManufacturingOrdersFlow: null,
    purchaseOrdersPreview: null,
    purchaseOrdersFlow: null,
    confirmSummary: null,
  };
}

function getReadyToImportCount(files: ImportCenterFileItem[]): number {
  return files.filter((item) => item.previewStatus === 'ready').length;
}

function getRequiresReviewCount(files: ImportCenterFileItem[]): number {
  return files.filter(
    (item) =>
      item.previewStatus === 'needs_mapping' ||
      item.previewStatus === 'needs_resolution' ||
      item.previewStatus === 'needs_review',
  ).length;
}

type DailyOutcomeKind = 'updated' | 'unchanged' | 'warning' | 'error';

type DailyOutcome = {
  kind: DailyOutcomeKind;
  label: string;
  color: 'success' | 'warning' | 'error' | 'default';
};

// Clasificación SOLO visual. Los estados y validaciones de negocio existentes
// siguen siendo la fuente de verdad para publicar y gestionar incidencias.
function getDailyOutcome(item: ImportCenterFileItem): DailyOutcome | null {
  if (item.previewStatus === 'error') {
    return { kind: 'error', label: 'No publicado', color: 'error' };
  }
  if (item.previewStatus !== 'imported' && item.previewStatus !== 'imported_with_issues') {
    return null;
  }

  const entries = item.detectedType === 'direct-inputs'
    ? item.directInputsFlow?.confirmResult
    : null;
  if (entries && entries.rows_imported === 0 &&
      entries.rows_skipped_existing > 0 &&
      entries.rows_needing_review === 0 &&
      !(entries.rows_replaced ?? 0) &&
      entries.persistence_errors.length === 0) {
    return { kind: 'unchanged', label: 'Sin cambios', color: 'default' };
  }

  // IND-019: publicado con avisos de datos = «Actualizado»; los avisos van a
  // «Revisa tus ficheros».
  if (item.previewStatus === 'imported_with_issues') {
    return { kind: 'warning', label: 'Actualizado', color: 'success' };
  }
  return { kind: 'updated', label: 'Actualizado', color: 'success' };
}

// IND-004: tarjeta de la carga diaria (estado, fecha de los datos, descartes).
function getDailyRunFile(item: ImportCenterFileItem): DailyLoadRunFile {
  const preview = item.detectedType === 'production'
    ? item.productionPreview
    : item.detectedType === 'imported-manufacturing-orders'
      ? item.importedManufacturingOrdersPreview
      : null;
  // IND-019: en la carga diaria los productos nuevos de producción se dan de
  // alta solos; no son «producto no reconocido».
  const unresolvedProductRefs = (item.detectedType === 'production' ? [] : preview?.detected_product_refs ?? [])
    .filter((ref) => !(item.productResolution[ref.source_value] || ref.resolved_product_id))
    .map((ref) => ref.source_value);

  return buildDailyRunFile({
    sourceKey: item.detectedType,
    fileName: item.file.name,
    fileLastModified: item.file.lastModified ?? null,
    outcomeKind: getDailyOutcome(item)?.kind ?? null,
    previewStatus: item.previewStatus,
    previewError: item.previewError,
    confirmSummary: item.confirmSummary,
    statusDetail: getStatusDetail(item),
    productionDateMax: item.productionPreview?.detected_date_max ?? null,
    unresolvedProductRefs,
    productionConfirm: item.productionFlow?.confirmResult ?? null,
    directInputsConfirm: item.directInputsFlow?.confirmResult ?? null,
    ofConfirm: item.importedManufacturingOrdersFlow?.confirmResult ?? null,
    purchaseConfirm: item.purchaseOrdersFlow?.confirmResult ?? null,
    // IND-019
    missingFieldLabels: item.missingRequiredFields.map(formatFieldLabel),
    purchaseRowErrors: item.purchaseOrdersPreview?.row_errors ?? [],
    ofBlockingIssues: item.importedManufacturingOrdersPreview?.blocking_issues ?? [],
    directInputsRowsNeedingReview: item.directInputsPreview?.idempotency_preview?.rows_needing_review ?? 0,
  });
}

function scrollToFileReview() {
  document.getElementById('revisa-tus-ficheros')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// IND-019: la carga no enseña avisos de datos; solo dice si hay pendientes
// (Bloquea o Cifra incompleta) y lleva a «Revisa tus ficheros».
function PendingNoticesLine() {
  const reviewQuery = useQuery({
    queryKey: PLANT_DATA_REVIEW_QUERY_KEY,
    queryFn: getPlantDataReview,
    staleTime: 60_000,
    retry: false,
  });
  const counts = reviewQuery.data?.counts;
  if (!counts || (counts.blocks ?? 0) + (counts.incomplete ?? 0) <= 0) return null;
  return (
    <Typography sx={{ fontSize: 12, color: '#fbbf24' }}>
      {'Hay avisos de datos pendientes · '}
      <Button
        size="small"
        variant="text"
        onClick={scrollToFileReview}
        sx={{ p: 0, minWidth: 0, fontSize: 12, textTransform: 'none', verticalAlign: 'baseline' }}
      >
        Ver en Revisa tus ficheros
      </Button>
    </Typography>
  );
}

function getStatusDetail(item: ImportCenterFileItem): string {
  if (item.previewStatus === 'needs_review') {
    const entries = item.directInputsPreview?.idempotency_preview;
    if (item.detectedType === 'direct-inputs' && entries) {
      return `${entries.rows_needing_review} línea(s) de albaranes requieren revisión. ` +
        'No se publicarán hasta resolver la diferencia con los registros existentes.';
    }
    // IND-017: las OF tienen su propia rama, con sus incidencias reales.
    if (item.detectedType === 'imported-manufacturing-orders') {
      const issues = item.importedManufacturingOrdersPreview?.blocking_issues ?? [];
      const kinds = Array.from(new Set(issues.map((issue) => issue.field_label || issue.message))).slice(0, 3);
      return `${issues.length} incidencia(s) bloqueante(s) en OF pendientes` +
        (kinds.length > 0 ? ` (${kinds.join(' · ')})` : '') +
        '. Corrige el origen y vuelve a analizar; no se publicarán las OF.';
    }
    const count = item.purchaseOrdersPreview?.row_errors.length ?? 0;
    return `${count} fila(s) bloqueante(s) en Compras. Corrige el origen y vuelve a analizar; no se publicará este snapshot.`;
  }

  if (item.previewStatus === 'needs_mapping') {
    return 'El archivo se ha leído, pero faltan columnas obligatorias por mapear.';
  }

  if (item.previewStatus === 'needs_resolution') {
    return 'El archivo es compatible, pero quedan referencias sin homologar.';
  }

  if (item.previewStatus === 'ready') {
    return 'El archivo está validado y listo para confirmar importación.';
  }

  if (item.previewStatus === 'imported') {
    return item.confirmSummary ?? 'Importación completada correctamente.';
  }

  if (item.previewStatus === 'imported_with_issues') {
    return item.confirmSummary ?? 'Importación completada con incidencias.';
  }

  if (item.previewStatus === 'error') {
    return item.previewError ?? 'No se pudo analizar o importar el archivo.';
  }

  if (item.previewStatus === 'skipped') {
    return 'Este archivo no se procesará en el lote actual.';
  }

  if (item.previewStatus === 'loading' || item.previewStatus === 'importing') {
    return 'El sistema está procesando este archivo.';
  }

  if (item.detectedType === 'unknown') {
    return 'El archivo aún no tiene un importador asignado. Revisa la detección y asígnalo manualmente si hace falta.';
  }

  return 'Pendiente de análisis.';
}

function getNextActionMessage(item: ImportCenterFileItem): string {
  if (item.previewStatus === 'needs_review') {
    return 'Revisa las filas bloqueantes del archivo y vuelve a cargar la versión corregida.';
  }

  if (item.previewStatus === 'error') {
    return 'Revisa el error y vuelve a analizar el archivo.';
  }

  if (item.detectedType === 'unknown') {
    return 'Asigna manualmente el tipo correcto o deja que el sistema lo reevalúe.';
  }

  if (item.previewStatus === 'needs_mapping') {
    return 'Completa el mapeo de columnas obligatorias para poder continuar.';
  }

  if (item.previewStatus === 'needs_resolution') {
    return 'Resuelve las homologaciones pendientes antes de confirmar.';
  }

  if (item.previewStatus === 'ready') {
    return 'Puedes confirmar este archivo o dejarlo listo para la importación por lote.';
  }

  if (item.previewStatus === 'importing' || item.previewStatus === 'loading') {
    return 'Espera a que termine el procesamiento actual.';
  }

  if (item.previewStatus === 'imported') {
    return 'No requiere acción adicional salvo revisión final si lo consideras necesario.';
  }

  if (item.previewStatus === 'imported_with_issues') {
    return 'Revisa las incidencias importadas y decide si necesitas corregir y relanzar.';
  }

  if (item.previewStatus === 'skipped') {
    return 'Este archivo está fuera del lote actual. Reincorpóralo solo si quieres procesarlo.';
  }

  return 'Revisa el archivo y confirma que su clasificación y configuración sean correctas.';
}

function getBatchStatus(summary: {
  total: number;
  readyCount: number;
  requiresActionCount: number;
  importedTotalCount: number;
}): 'empty' | 'requires_action' | 'ready' | 'completed' | 'partially_completed' {
  if (summary.total === 0) {
    return 'empty';
  }

  if (summary.requiresActionCount > 0 && summary.importedTotalCount > 0) {
    return 'partially_completed';
  }

  if (summary.requiresActionCount > 0) {
    return 'requires_action';
  }

  if (summary.readyCount > 0) {
    return 'ready';
  }

  if (summary.importedTotalCount > 0) {
    return 'completed';
  }

  return 'empty';
}

function getBatchStatusAlertConfig(summary: {
  total: number;
  readyCount: number;
  requiresActionCount: number;
  outOfBatchCount: number;
  importedTotalCount: number;
}): {
  severity: 'info' | 'warning' | 'success';
  title: string;
  message: string;
  actionLabel: string | null;
} {
  const batchStatus = getBatchStatus(summary);
  const processableCount = summary.total - summary.outOfBatchCount;
  const isAutoResolvedReadyBatch =
    batchStatus === 'ready' &&
    processableCount > 0 &&
    summary.readyCount === processableCount;
  const isFullyAutoResolvedBatch =
    batchStatus === 'completed' &&
    processableCount > 0 &&
    summary.importedTotalCount === processableCount;

  if (batchStatus === 'partially_completed') {
    return {
      severity: 'warning',
      title: 'Lote parcialmente actualizado',
      message:
        'Se han publicado algunos archivos tras confirmarlos y quedan otros pendientes de revisión.',
      actionLabel: null,
    };
  }

  if (batchStatus === 'requires_action') {
    return {
      severity: 'warning',
      title: 'Lote con acciones pendientes',
      message:
        'El sistema ha analizado el lote, pero todavía hay archivos que requieren revisión, mapeo, homologación o asignación.',
      actionLabel: null,
    };
  }

  if (isAutoResolvedReadyBatch) {
    return {
      severity: 'success',
      title: 'Lote auto-resuelto',
      message:
        'No queda ninguna intervención manual pendiente en los archivos procesables del lote. Solo falta confirmar la importación.',
      actionLabel: 'Importar lote listo',
    };
  }

  if (batchStatus === 'ready') {
    return {
      severity: 'success',
      title: 'Lote listo para importar',
      message:
        'No quedan incidencias pendientes relevantes y ya puedes confirmar directamente los archivos listos o lanzar la importación por lote.',
      actionLabel: 'Importar lote listo',
    };
  }

  if (isFullyAutoResolvedBatch) {
    return {
      severity: 'success',
      title: 'Lote auto-resuelto',
      message:
        'Todos los archivos procesables del lote se han publicado tras confirmación. Puedes revisar el resultado.',
      actionLabel: null,
    };
  }

  if (batchStatus === 'completed') {
    return {
      severity: 'success',
      title: 'Lote ya procesado',
      message:
        'Los archivos del lote ya han sido procesados o revisados. Solo hace falta inspección final si quieres validar el resultado.',
      actionLabel: null,
    };
  }

  return {
    severity: 'info',
    title: 'Esperando lote',
    message:
      'Selecciona una carpeta para que el sistema analice los archivos e intente clasificarlos automáticamente.',
    actionLabel: null,
  };
}

function shouldShowExpandedDetailByDefault(item: ImportCenterFileItem | null): boolean {
  if (!item) return false;

  if (item.detectedType === 'unknown') return true;

  return (
    item.previewStatus === 'error' ||
    item.previewStatus === 'needs_mapping' ||
    item.previewStatus === 'needs_resolution' ||
    item.previewStatus === 'needs_review' ||
    item.previewStatus === 'idle' ||
    item.previewStatus === 'loading' ||
    item.previewStatus === 'importing'
  );
}

function getFileNameTokensForLearning(path: string): string[] {
  return path
    .toLowerCase()
    .replace(/\.[^.]+$/, '')
    .split(/[^a-z0-9áéíóúüñ]+/i)
    .map((token) => token.trim())
    .filter((token) => token.length >= 4);
}

function getFileDirectoryForLearning(path: string): string {
  const normalizedPath = path.replace(/\\/g, '/');
  const lastSlashIndex = normalizedPath.lastIndexOf('/');

  if (lastSlashIndex === -1) {
    return '';
  }

  return normalizedPath.slice(0, lastSlashIndex).toLowerCase();
}

function getSharedLearningTokenCount(
  sourcePath: string,
  targetPath: string,
): number {
  const sourceTokens = getFileNameTokensForLearning(sourcePath);
  const targetTokens = getFileNameTokensForLearning(targetPath);

  if (sourceTokens.length === 0 || targetTokens.length === 0) {
    return 0;
  }

  return sourceTokens.filter((token) => targetTokens.includes(token)).length;
}

function getSimilarityScoreForImporterLearning(
  sourcePath: string,
  targetPath: string,
): number {
  const sharedTokens = getSharedLearningTokenCount(sourcePath, targetPath);
  const sourceDirectory = getFileDirectoryForLearning(sourcePath);
  const targetDirectory = getFileDirectoryForLearning(targetPath);
  const sameDirectory =
    Boolean(sourceDirectory) &&
    Boolean(targetDirectory) &&
    sourceDirectory === targetDirectory;

  return sharedTokens + (sameDirectory ? 2 : 0);
}

function getColumnOverlapScore(
  sourceColumns: string[],
  targetColumns: string[],
): number {
  if (sourceColumns.length === 0 || targetColumns.length === 0) {
    return 0;
  }

  const normalizedSource = sourceColumns.map((column) => column.trim().toLowerCase());
  const normalizedTarget = targetColumns.map((column) => column.trim().toLowerCase());

  return normalizedSource.filter((column) => normalizedTarget.includes(column)).length;
}

function getMappingStructureScore(
  sourceItem: ImportCenterFileItem,
  targetItem: ImportCenterFileItem,
): number {
  const sourceMappedFields = Object.entries(sourceItem.mapping)
    .filter(([, value]) => Boolean(value))
    .map(([field]) => field);

  const targetMappedFields = Object.entries(targetItem.mapping)
    .filter(([, value]) => Boolean(value))
    .map(([field]) => field);

  if (sourceMappedFields.length === 0 || targetMappedFields.length === 0) {
    return 0;
  }

  return sourceMappedFields.filter((field) => targetMappedFields.includes(field)).length;
}

function getLearningCompatibilityScore(
  sourceItem: ImportCenterFileItem,
  targetItem: ImportCenterFileItem,
): number {
  const nameSimilarityScore = getSimilarityScoreForImporterLearning(
    sourceItem.relativePath,
    targetItem.relativePath,
  );
  const columnOverlapScore = getColumnOverlapScore(
    sourceItem.detectedColumns,
    targetItem.detectedColumns,
  );
  const mappingStructureScore = getMappingStructureScore(sourceItem, targetItem);

  return (
    nameSimilarityScore * 10 +
    columnOverlapScore * 4 +
    mappingStructureScore * 6
  );
}

function areFilesSimilarForImporterLearning(
  sourceItem: ImportCenterFileItem,
  targetItem: ImportCenterFileItem,
): boolean {
  return getLearningCompatibilityScore(sourceItem, targetItem) >= 20;
}

function canPropagateLearningFromItem(item: ImportCenterFileItem): boolean {
  return (
    item.detectedType !== 'unknown' &&
    item.detectedType !== 'excluded' &&
    (
      item.previewStatus === 'ready' ||
      item.previewStatus === 'importing' ||
      item.previewStatus === 'imported' ||
      item.previewStatus === 'imported_with_issues'
    )
  );
}

function shouldReplaceLearnedSuggestion(
  item: ImportCenterFileItem,
  importer: Exclude<ImportCenterDetectedType, 'unknown' | 'excluded'>,
  score: number,
): boolean {
  if (!item.learnedSuggestedImporter) {
    return true;
  }

  if (item.learnedSuggestedImporter !== importer) {
    return item.learnedSuggestionScore === null || score > item.learnedSuggestionScore;
  }

  return item.learnedSuggestionScore === null || score > item.learnedSuggestionScore;
}

function shouldUseItemForLearningPropagation(item: ImportCenterFileItem): boolean {
  return (
    canPropagateLearningFromItem(item) &&
    item.detectedColumns.length > 0
  );
}

const IMPORT_CENTER_LEARNED_IMPORTERS_STORAGE_KEY =
  'industrial-cost-import-center-learned-importers-v1';

const IMPORT_CENTER_PERSISTED_SUGGESTION_MIN_SCORE = 250;
const IMPORT_CENTER_PERSISTED_SUGGESTION_STRONG_SCORE = 500;
const IMPORT_CENTER_PERSISTED_SUGGESTION_MAX_AGE_MS =
  1000 * 60 * 60 * 24 * 90;

type PersistedImportCenterLearnedSuggestion = {
  signature: string;
  structuralSignature: string | null;
  importer: Exclude<ImportCenterDetectedType, 'unknown' | 'excluded'>;
  score: number;
  updatedAt: number;
};

function getLearningSignatureForPath(path: string): string {
  const tokens = getFileNameTokensForLearning(path).sort().slice(0, 6);
  const directory = getFileDirectoryForLearning(path);

  return `${directory}::${tokens.join('|')}`;
}

function getStructuralSignatureFromItem(
  item: Pick<ImportCenterFileItem, 'detectedColumns' | 'mapping'>,
): string | null {
  const normalizedColumns = [...item.detectedColumns]
    .map((column) => column.trim().toLowerCase())
    .filter(Boolean)
    .sort()
    .slice(0, 12);

  const mappedFields = Object.entries(item.mapping)
    .filter(([, value]) => Boolean(value))
    .map(([field]) => field.trim().toLowerCase())
    .filter(Boolean)
    .sort();

  if (normalizedColumns.length === 0 && mappedFields.length === 0) {
    return null;
  }

  return `${normalizedColumns.join('|')}::${mappedFields.join('|')}`;
}

function loadPersistedImportCenterLearnedSuggestions(): PersistedImportCenterLearnedSuggestion[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const rawValue = window.localStorage.getItem(
      IMPORT_CENTER_LEARNED_IMPORTERS_STORAGE_KEY,
    );

    if (!rawValue) {
      return [];
    }

    const parsed = JSON.parse(rawValue);

    if (!Array.isArray(parsed)) {
      return [];
    }

    const now = Date.now();

    return parsed
      .filter(
        (item): item is PersistedImportCenterLearnedSuggestion =>
          Boolean(item) &&
          typeof item.signature === 'string' &&
          (typeof item.structuralSignature === 'string' ||
            item.structuralSignature === null ||
            typeof item.structuralSignature === 'undefined') &&
          typeof item.importer === 'string' &&
          typeof item.score === 'number' &&
          typeof item.updatedAt === 'number',
      )
      .map((item) => ({
        ...item,
        structuralSignature:
          typeof item.structuralSignature === 'string'
            ? item.structuralSignature
            : null,
      }))
      .filter(
        (item) =>
          item.score >= IMPORT_CENTER_PERSISTED_SUGGESTION_MIN_SCORE &&
          now - item.updatedAt <= IMPORT_CENTER_PERSISTED_SUGGESTION_MAX_AGE_MS,
      );
  } catch {
    return [];
  }
}


function savePersistedImportCenterLearnedSuggestions(
  suggestions: PersistedImportCenterLearnedSuggestion[],
): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      IMPORT_CENTER_LEARNED_IMPORTERS_STORAGE_KEY,
      JSON.stringify(suggestions.slice(0, 100)),
    );
  } catch {
    // no-op
  }
}

function getPersistedLearnedSuggestionForItem(
  item: Pick<ImportCenterFileItem, 'relativePath' | 'detectedColumns' | 'mapping'>,
): PersistedImportCenterLearnedSuggestion | null {
  const signature = getLearningSignatureForPath(item.relativePath);
  const structuralSignature = getStructuralSignatureFromItem(item);
  const suggestions = loadPersistedImportCenterLearnedSuggestions();

  if (structuralSignature) {
    const structuralMatch =
      suggestions.find(
        (entry) => entry.structuralSignature === structuralSignature,
      ) ?? null;

    if (structuralMatch) {
      return structuralMatch;
    }
  }

  return suggestions.find((entry) => entry.signature === signature) ?? null;
}

function rememberPersistedLearnedSuggestion(
  item: Pick<ImportCenterFileItem, 'relativePath' | 'detectedColumns' | 'mapping'>,
  importer: Exclude<ImportCenterDetectedType, 'unknown' | 'excluded'>,
  score: number,
): void {
  if (score < IMPORT_CENTER_PERSISTED_SUGGESTION_MIN_SCORE) {
    return;
  }

  const signature = getLearningSignatureForPath(item.relativePath);
  const structuralSignature = getStructuralSignatureFromItem(item);
  const suggestions = loadPersistedImportCenterLearnedSuggestions();
  const existingSuggestion = suggestions.find(
    (entry) =>
      entry.signature === signature ||
      (
        structuralSignature !== null &&
        entry.structuralSignature === structuralSignature
      ),
  );

  if (existingSuggestion && existingSuggestion.score > score) {
    return;
  }

  const nextSuggestions = [
    {
      signature,
      structuralSignature,
      importer,
      score,
      updatedAt: Date.now(),
    },
    ...suggestions.filter(
      (entry) =>
        entry.signature !== signature &&
        (
          structuralSignature === null ||
          entry.structuralSignature !== structuralSignature
        ),
    ),
  ].sort((a, b) => b.updatedAt - a.updatedAt);

  savePersistedImportCenterLearnedSuggestions(nextSuggestions);
}

function getPersistenceScoreFromItem(item: ImportCenterFileItem): number {
  const mappedFieldsScore =
    Object.values(item.mapping).filter((value) => Boolean(value)).length * 100;
  const missingRequiredPenalty = item.missingRequiredFields.length * 10;
  const detectedColumnsScore = item.detectedColumns.length;

  const baseStructuralScore = Math.max(
    1,
    mappedFieldsScore + detectedColumnsScore - missingRequiredPenalty,
  );

  if (item.previewStatus === 'imported') {
    return baseStructuralScore + 500;
  }

  if (item.previewStatus === 'imported_with_issues') {
    return baseStructuralScore + 300;
  }

  if (item.previewStatus === 'ready') {
    return baseStructuralScore + 200;
  }

  return baseStructuralScore;
}

function shouldPersistLearnedSuggestion(item: ImportCenterFileItem): boolean {
  return (
    canPropagateLearningFromItem(item) &&
    item.detectedColumns.length > 0 &&
    item.mapping &&
    Object.keys(item.mapping).length > 0
  );
}

// El aprendizaje de clasificación se guarda solo tras una publicación confirmada.
// No usar desde el análisis de archivos ni desde cambios de mapeo.
function rememberImporterAfterConfirmedPublication(item: ImportCenterFileItem): void {
  if (!shouldPersistLearnedSuggestion(item)) {
    return;
  }

  if (item.detectedType === 'unknown' || item.detectedType === 'excluded') {
    return;
  }

  rememberPersistedLearnedSuggestion(
    item,
    item.detectedType,
    getPersistenceScoreFromItem(item),
  );
}

function sortResolutionItems<T>(
  items: T[],
  isResolved: (item: T) => boolean,
): T[] {
  return [...items].sort((a, b) => {
    const aResolved = isResolved(a);
    const bResolved = isResolved(b);

    if (aResolved !== bResolved) {
      return aResolved ? 1 : -1;
    }

    return 0;
  });
}

function getSortedMappingEntries(
  mapping: Record<string, string | null>,
  missingRequiredFields: string[],
): Array<[string, string | null]> {
  const missingRequiredSet = new Set(missingRequiredFields);

  return Object.entries(mapping).sort(([fieldA, valueA], [fieldB, valueB]) => {
    const aIsRequired = missingRequiredSet.has(fieldA);
    const bIsRequired = missingRequiredSet.has(fieldB);

    const aAssigned = Boolean(valueA);
    const bAssigned = Boolean(valueB);

    const getPriority = (isRequired: boolean, assigned: boolean): number => {
      if (isRequired && !assigned) return 400;
      if (isRequired && assigned) return 300;
      if (!isRequired && !assigned) return 200;
      return 100;
    };

    const priorityDiff =
      getPriority(bIsRequired, bAssigned) - getPriority(aIsRequired, aAssigned);

    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    return fieldA.localeCompare(fieldB, 'es');
  });
}

type IndustrialCostImportCenterViewProps = {
  compact?: boolean;
  onImported?: () => void;
  sourceStatuses?: IndustrialCostDataSourceStatusItem[];
  sourceStatusesLoading?: boolean;
  sourceStatusesError?: boolean;
};

export default function IndustrialCostImportCenterView({
  compact = false,
  onImported,
  sourceStatuses,
  sourceStatusesLoading = false,
  sourceStatusesError = false,
}: IndustrialCostImportCenterViewProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [files, setFiles] = useState<ImportCenterFileItem[]>([]);
  const [activeFileId, setActiveFileId] = useState<string | null>(null);

  const [products, setProducts] = useState<Product[]>([]);
  const [materials, setMaterials] = useState<DirectMaterial[]>([]);
  const [loadingReferenceData, setLoadingReferenceData] = useState(true);

  const [loadingFolder, setLoadingFolder] = useState(false);
  const [confirmingBatch, setConfirmingBatch] = useState(false);

  const [pageError, setPageError] = useState<string | null>(null);
  const [pageSuccess, setPageSuccess] = useState<string | null>(null);

  const activeProducts = useMemo(
    () => products.filter((product) => product.is_active),
    [products],
  );

  const activeMaterials = useMemo(
    () => materials.filter((material) => material.is_active),
    [materials],
  );

  useEffect(() => {
    async function loadReferenceData() {
      setLoadingReferenceData(true);

      try {
        const [productsData, materialsData] = await Promise.all([
          getIndustrialCostProducts(),
          getIndustrialCostDirectMaterials(),
        ]);

        setProducts(productsData);
        setMaterials(materialsData);
      } catch (error) {
        setPageError(
          getErrorMessage(
            error,
            'No se pudieron cargar productos y materiales para homologación.',
          ),
        );
      } finally {
        setLoadingReferenceData(false);
      }
    }

    void loadReferenceData();
  }, []);

  // IND-002: resultado de la última carga diaria, también tras recargar.
  const [lastDailyRun, setLastDailyRun] = useState<DailyLoadRun | null>(null);
  // IND-007
  const canImport = useCanManageImports();
  useEffect(() => {
    if (!compact) return;
    getLatestDailyLoadRun()
      .then(setLastDailyRun)
      .catch(() => setLastDailyRun(null));
  }, [compact]);

  useEffect(() => {
    if (files.length === 0) {
      setActiveFileId(null);
      return;
    }

    if (!activeFileId || !files.some((item) => item.id === activeFileId)) {
      setActiveFileId(getPreferredActiveFileId(files));
    }
  }, [files, activeFileId]);

  const activeFile = useMemo(
    () => files.find((item) => item.id === activeFileId) ?? null,
    [files, activeFileId],
  );

  const persistedSuggestionForActiveFile = useMemo(() => {
    if (!activeFile) {
      return null;
    }

    return getPersistedLearnedSuggestionForItem(activeFile);
  }, [activeFile]);

  const [showResolvedFileDetails, setShowResolvedFileDetails] = useState(false);

  useEffect(() => {
    setShowResolvedFileDetails(false);
  }, [activeFileId]);

  const shouldShowExpandedActiveDetail = useMemo(() => {
    return (
      shouldShowExpandedDetailByDefault(activeFile) || showResolvedFileDetails
    );
  }, [activeFile, showResolvedFileDetails]);

  const summary = useMemo(() => {
    const countByImporter = getImportCenterVisibleImporters().reduce<
      Record<string, number>
    >((acc, importer) => {
      acc[importer.key] = files.filter(
        (item) => item.detectedType === importer.key,
      ).length;
      return acc;
    }, {});

    const readyCount = getReadyToImportCount(files);
    const requiresReviewCount = getRequiresReviewCount(files);
    const requiresActionCount = files.filter(
      (item) =>
        item.previewStatus === 'error' ||
        item.previewStatus === 'needs_mapping' ||
        item.previewStatus === 'needs_resolution' ||
        item.previewStatus === 'needs_review' ||
        item.detectedType === 'unknown' ||
        item.previewStatus === 'idle',
    ).length;
    const outOfBatchCount = files.filter(
      (item) =>
        item.detectedType === 'excluded' ||
        item.previewStatus === 'skipped',
    ).length;
    const autoImportedOkCount = files.filter(
      (item) => item.previewStatus === 'imported',
    ).length;
    const autoImportedWithIssuesCount = files.filter(
      (item) => item.previewStatus === 'imported_with_issues',
    ).length;
    const importedTotalCount = autoImportedOkCount + autoImportedWithIssuesCount;

    return {
      total: files.length,
      countByImporter,
      readyCount,
      requiresReviewCount,
      requiresActionCount,
      outOfBatchCount,
      autoImportedOkCount,
      autoImportedWithIssuesCount,
      importedTotalCount,
    };
  }, [files]);

  const groupedFiles = useMemo(() => {
    const importerGroups = getImportCenterVisibleImporters().map((importer) => ({
      importerKey: importer.key,
      title: importer.label,
      helper: importer.description,
      items: sortImportCenterItemsByAttention(
        files.filter((item) => item.detectedType === importer.key),
      ),
    }));

    const pendingReviewGroup = {
      importerKey: 'pending-review',
      title: 'Pendientes de asignación o revisión',
      helper: 'Archivos no reconocidos todavía o con error que requieren atención.',
      items: sortImportCenterItemsByAttention(
        files.filter(
          (item) =>
            item.detectedType === 'unknown' ||
            item.previewStatus === 'error',
        ),
      ),
    };

    const outOfBatchGroup = {
      importerKey: 'out-of-batch',
      title: 'Fuera del lote',
      helper: 'Archivos excluidos manualmente o no procesables en el lote actual.',
      items: sortImportCenterItemsByAttention(
        files.filter(
          (item) =>
            item.detectedType === 'excluded' ||
            item.previewStatus === 'skipped',
        ),
      ),
    };

    return {
      importerGroups,
      pendingReviewGroup,
      outOfBatchGroup,
    };
  }, [files]);

  const batchStatusAlert = useMemo(() => getBatchStatusAlertConfig(summary), [summary]);

  const handleOpenFolderPicker = () => {
    inputRef.current?.click();
  };

  async function analyzeFileItemByDetectedType(
    item: ImportCenterFileItem,
  ): Promise<ImportCenterFileItem> {
    if (item.detectedType === 'production') {
      const normalization = await analyzeFileNormalization(
        item.file,
        'production_volumes',
      ).catch(() => null);

      const baseFlow =
        item.productionFlow ??
        createProductionImportFlowState(item.file, item.productionConfig);

      const normalizedBaseFlow = normalization
        ? {
            ...baseFlow,
            normalizationSheetName: normalization.selected_sheet,
            normalizationHeaderRowIndex: normalization.header_row_index,
          }
        : baseFlow;

      const flowState = await runProductionImportPreview(normalizedBaseFlow, {
        file: item.file,
        mapping: normalization?.applied_column_mapping ?? item.mapping,
        productResolution: item.productResolution,
        config: item.productionConfig,
      });

      let nextStatus: ImportCenterPreviewStatus = 'ready';

      if (flowState.status === 'needs_mapping') {
        nextStatus = 'needs_mapping';
      } else if (flowState.status === 'needs_resolution') {
        nextStatus = 'needs_resolution';
      } else if (flowState.status === 'error') {
        nextStatus = 'error';
      } else if (flowState.status === 'idle') {
        nextStatus = 'idle';
      } else if (!canContinueProductionImport(flowState)) {
        nextStatus = 'needs_mapping';
      } else {
        const unresolvedRefs = getProductionUnresolvedProductRefs(flowState);
        nextStatus = unresolvedRefs.length > 0 ? 'needs_resolution' : 'ready';
      }

      return {
        ...item,
        previewStatus: nextStatus,
        previewError: flowState.error,
        missingRequiredFields: flowState.preview?.missing_required_fields ?? [],
        detectedColumns: flowState.preview?.columns ?? [],
        previewRows: (flowState.preview?.preview_rows ?? []) as Array<Record<string, unknown>>,
        mapping: flowState.mapping,
        productResolution: flowState.productResolution,
        productionConfig: flowState.config,
        productionPreview: flowState.preview,
        productionFlow: flowState,
        directInputsPreview: null,
        importedManufacturingOrdersPreview: null,
        purchaseOrdersPreview: null,
        confirmSummary: null,
      };
    }

    if (item.detectedType === 'direct-inputs') {
      const normalization = await analyzeFileNormalization(
        item.file,
        'direct_material_entries',
      ).catch(() => null);

      const baseFlow =
        item.directInputsFlow ??
        createDirectInputsImportFlowState(item.file);

      const flowState = await runDirectInputsImportPreview(baseFlow, {
        file: item.file,
        mapping: normalization?.applied_column_mapping ?? item.mapping,
        materialResolution: item.materialResolution,
        normalization: normalization
          ? {
              sheet_name: normalization.selected_sheet,
              header_row_index: normalization.header_row_index,
            }
          : undefined,
      });

      const flowSummary = getDirectInputsImportSummary(flowState);

      let nextStatus: ImportCenterPreviewStatus = 'ready';

      if (flowState.status === 'needs_mapping') {
        nextStatus = 'needs_mapping';
      } else if (flowState.status === 'error') {
        nextStatus = 'error';
      } else if (flowState.status === 'idle') {
        nextStatus = 'idle';
      } else if (!flowSummary.canContinue) {
        nextStatus = 'needs_mapping';
      } else if (!flowState.preview?.idempotency_preview) {
        nextStatus = 'error';
      } else {
        // IND-002: los albaranes modificados se sustituyen por el Excel y los
        // ambiguos se omiten con aviso al confirmar; no bloquean la carga.
        nextStatus = 'ready';
      }

      return {
        ...item,
        previewStatus: nextStatus,
        previewError: flowState.error,
        missingRequiredFields: flowState.preview?.missing_required_fields ?? [],
        detectedColumns: flowState.preview?.columns ?? [],
        previewRows: (flowState.preview?.preview_rows ?? []) as Array<Record<string, unknown>>,
        mapping: flowState.mapping,
        materialResolution: flowState.materialResolution,
        directInputsPreview: flowState.preview,
        directInputsFlow: flowState,
        productionPreview: null,
        importedManufacturingOrdersPreview: null,
        purchaseOrdersPreview: null,
        confirmSummary: null,
      };
    }

    if (item.detectedType === 'purchase-orders') {
      const normalization = await analyzeFileNormalization(
        item.file,
        'purchase_orders_pending',
      ).catch(() => null);

      const baseFlow = item.purchaseOrdersFlow ??
        createPurchaseOrdersImportFlowState(item.file);

      const flowState = await runPurchaseOrdersImportPreview(baseFlow, {
        file: item.file,
        mapping: Object.keys(item.mapping).length > 0
          ? item.mapping
          : (normalization?.applied_column_mapping ?? item.mapping),
        normalization: normalization
          ? {
              sheet_name: normalization.selected_sheet,
              header_row_index: normalization.header_row_index,
            }
          : undefined,
      });

      const summary = getPurchaseOrdersImportSummary(flowState);
      let nextStatus: ImportCenterPreviewStatus = 'ready';
      if (flowState.status === 'error') {
        nextStatus = 'error';
      } else if (flowState.status === 'idle') {
        nextStatus = 'idle';
      } else if (flowState.status === 'needs_mapping' || summary.missingRequiredFieldsCount > 0) {
        nextStatus = 'needs_mapping';
      } else if (summary.rowErrorsCount > 0) {
        // Solo priorizamos el bloqueo por filas cuando el esquema está completo.
        nextStatus = 'needs_review';
      } else if (!summary.canContinue) {
        nextStatus = 'needs_mapping';
      }

      return {
        ...item,
        previewStatus: nextStatus,
        previewError: flowState.error,
        missingRequiredFields: flowState.preview?.missing_required_fields ?? [],
        detectedColumns: flowState.preview?.columns ?? [],
        previewRows: (flowState.preview?.preview_rows ?? []) as Array<Record<string, unknown>>,
        mapping: flowState.mapping,
        purchaseOrdersPreview: flowState.preview,
        purchaseOrdersFlow: flowState,
        productionPreview: null,
        directInputsPreview: null,
        importedManufacturingOrdersPreview: null,
        confirmSummary: null,
      };
    }

    const normalization = await analyzeFileNormalization(
      item.file,
      'imported_manufacturing_orders',
    ).catch(() => null);

    const baseFlow =
      item.importedManufacturingOrdersFlow ??
      createImportedManufacturingOrdersImportFlowState(item.file);

    const flowState = await runImportedManufacturingOrdersImportPreview(baseFlow, {
      file: item.file,
      mapping: normalization?.applied_column_mapping ?? item.mapping,
      productResolution: item.productResolution,
      normalization: normalization
        ? {
            sheet_name: normalization.selected_sheet,
            header_row_index: normalization.header_row_index,
          }
        : undefined,
    });

    const flowSummary =
      getImportedManufacturingOrdersImportSummary(flowState);

    let nextStatus: ImportCenterPreviewStatus = 'ready';

    if (flowState.status === 'needs_mapping') {
      nextStatus = 'needs_mapping';
    } else if (flowState.status === 'error') {
      nextStatus = 'error';
    } else if (flowState.status === 'idle') {
      nextStatus = 'idle';
    } else if (!flowSummary.canContinue) {
      // IC-DATA-11B2A: las incidencias canónicas bloquean; las referencias
      // de producto sin homologar no bloquean, igual que en carga individual.
      nextStatus = flowSummary.canonicalBlockingIssuesCount > 0
        ? 'needs_review'
        : 'needs_mapping';
    } else {
      nextStatus = 'ready';
    }

    return {
      ...item,
      previewStatus: nextStatus,
      previewError: flowState.error,
      missingRequiredFields:
        flowState.preview?.missing_required_fields ?? [],
      detectedColumns: flowState.preview?.columns ?? [],
      previewRows: (
        flowState.preview?.preview_rows ?? []
      ) as Array<Record<string, unknown>>,
      mapping: flowState.mapping,
      productResolution: flowState.productResolution,
      importedManufacturingOrdersPreview: flowState.preview,
      importedManufacturingOrdersFlow: flowState,
      productionPreview: null,
      directInputsPreview: null,
      confirmSummary: null,
    };
  }

  function getPreviewStatusPriority(status: ImportCenterPreviewStatus): number {
    if (status === 'needs_review') return 6;
    if (status === 'ready') return 5;
    if (status === 'needs_resolution') return 4;
    if (status === 'needs_mapping') return 3;
    if (status === 'idle') return 2;
    if (status === 'error') return 1;
    return 0;
  }

  function getCandidateOrderForFile(
    item: ImportCenterFileItem,
  ): Array<Exclude<ImportCenterDetectedType, 'unknown' | 'excluded'>> {
    const learnedCandidates = item.learnedSuggestedImporter
      ? [item.learnedSuggestedImporter]
      : [];

    const detectedCandidates = getImportCenterOrderedCandidateKeys(item.detection).filter(
      (candidateKey) => !learnedCandidates.includes(candidateKey),
    );

    const visibleCandidates = getImportCenterVisibleImporters()
      .map((importer) => importer.key)
      .filter(
        (candidateKey) =>
          !learnedCandidates.includes(candidateKey) &&
          !detectedCandidates.includes(candidateKey),
      );

    if (item.detectedType === 'unknown' || item.detectedType === 'excluded') {
      return [...learnedCandidates, ...detectedCandidates, ...visibleCandidates];
    }

    const currentDetectedType = item.detectedType as Exclude<
      ImportCenterDetectedType,
      'unknown' | 'excluded'
    >;

    return [
      currentDetectedType,
      ...learnedCandidates.filter((candidateKey) => candidateKey !== currentDetectedType),
      ...detectedCandidates.filter((candidateKey) => candidateKey !== currentDetectedType),
      ...visibleCandidates.filter((candidateKey) => candidateKey !== currentDetectedType),
    ];
  }

  function getMappedFieldsCount(mapping: Record<string, string | null>): number {
    return Object.values(mapping).filter((value) => Boolean(value)).length;
  }

  function getAnalyzedCandidateStructuralScore(item: ImportCenterFileItem): number {
    const mappedFieldsScore = getMappedFieldsCount(item.mapping) * 100;
    const missingRequiredPenalty = item.missingRequiredFields.length * 10;
    const detectedColumnsScore = item.detectedColumns.length;

    return mappedFieldsScore + detectedColumnsScore - missingRequiredPenalty;
  }

  function getFileAttentionPriority(item: ImportCenterFileItem): number {
    if (item.previewStatus === 'error') return 100;
    if (item.previewStatus === 'needs_review') return 95;
    if (item.previewStatus === 'needs_mapping') return 90;
    if (item.previewStatus === 'needs_resolution') return 80;
    if (item.detectedType === 'unknown') return 70;
    if (item.previewStatus === 'idle') return 60;
    if (item.previewStatus === 'ready') return 50;
    if (item.previewStatus === 'importing') return 40;
    if (item.previewStatus === 'imported_with_issues') return 30;
    if (item.previewStatus === 'imported') return 20;
    if (item.previewStatus === 'skipped') return 10;
    return 0;
  }

  function sortImportCenterItemsByAttention(
    items: ImportCenterFileItem[],
  ): ImportCenterFileItem[] {
    return [...items].sort((a, b) => {
      const attentionDiff = getFileAttentionPriority(b) - getFileAttentionPriority(a);

      if (attentionDiff !== 0) {
        return attentionDiff;
      }

      if (a.autoSelectedBestCandidate !== b.autoSelectedBestCandidate) {
        return a.autoSelectedBestCandidate ? -1 : 1;
      }

      if (a.autoReassignedByDetection !== b.autoReassignedByDetection) {
        return a.autoReassignedByDetection ? -1 : 1;
      }

      return a.relativePath.localeCompare(b.relativePath, 'es');
    });
  }

  function getPreferredActiveFileId(
    items: ImportCenterFileItem[],
  ): string | null {
    if (items.length === 0) {
      return null;
    }

    return sortImportCenterItemsByAttention(items)[0]?.id ?? null;
  }

  function getStructuralDetectionConfidence(
    status: ImportCenterPreviewStatus,
    competingValidCandidates: number,
  ): ImportCenterDetectionResult['confidence'] {
    if (status === 'ready') {
      return competingValidCandidates > 1 ? 'medium' : 'high';
    }

    if (status === 'needs_resolution' || status === 'needs_review') {
      return competingValidCandidates > 1 ? 'low' : 'medium';
    }

    if (status === 'needs_mapping') {
      return 'low';
    }

    if (status === 'idle') {
      return 'low';
    }

    return 'none';
  }

  function buildStructuralDecisionReason(
    candidateKey: Exclude<ImportCenterDetectedType, 'unknown' | 'excluded'>,
    status: ImportCenterPreviewStatus,
    competingValidCandidates: number,
    item: ImportCenterFileItem,
  ): string {
    const importerLabel = getDetectedTypeLabel(candidateKey);
    const mappedFields = getMappedFieldsCount(item.mapping);
    const missingRequired = item.missingRequiredFields.length;

    if (status === 'ready') {
      return competingValidCandidates > 1
        ? `Asignación automática tras probar varios importadores y priorizar ${importerLabel} como mejor encaje estructural (${mappedFields} campos mapeados, ${missingRequired} obligatorios pendientes).`
        : `Asignación automática tras validar correctamente la estructura del archivo con el importador ${importerLabel} (${mappedFields} campos mapeados, ${missingRequired} obligatorios pendientes).`;
    }

    if (status === 'needs_resolution') {
      return competingValidCandidates > 1
        ? `Se prioriza ${importerLabel} como mejor encaje estructural (${mappedFields} campos mapeados, ${missingRequired} obligatorios pendientes), aunque el archivo todavía requiere homologación.`
        : `El archivo encaja con ${importerLabel} (${mappedFields} campos mapeados, ${missingRequired} obligatorios pendientes), pero todavía requiere homologación antes de quedar listo.`;
    }

    if (status === 'needs_review') {
      return `El importador ${importerLabel} reconoce las columnas del archivo, pero ha detectado filas bloqueantes; no se publicará mientras existan.`;
    }

    if (status === 'needs_mapping') {
      return competingValidCandidates > 1
        ? `Se prioriza ${importerLabel} como mejor encaje disponible (${mappedFields} campos mapeados, ${missingRequired} obligatorios pendientes), aunque el archivo todavía requiere completar el mapeo.`
        : `El archivo encaja mejor con ${importerLabel} (${mappedFields} campos mapeados, ${missingRequired} obligatorios pendientes), pero todavía requiere completar el mapeo.`;
    }

    return `Se selecciona ${importerLabel} como mejor candidato disponible para continuar la revisión (${mappedFields} campos mapeados, ${missingRequired} obligatorios pendientes).`;
  }

  async function analyzeFileItem(item: ImportCenterFileItem): Promise<ImportCenterFileItem> {
    // IC-DATA-11B5A: clasificación diaria estable.
    // Una fuente confirmada por historial o por el usuario NO se reasigna a otro
    // importador sólo porque un preview ajeno parezca estructuralmente compatible.
    if (item.lockedDailyImporter &&
        item.detectedType !== 'unknown' && item.detectedType !== 'excluded') {
      try {
        return await analyzeFileItemByDetectedType(item);
      } catch (error) {
        return {
          ...item,
          previewStatus: 'error',
          previewError: getErrorMessage(error, 'No se pudo analizar el archivo con su importador asignado.'),
          confirmSummary: null,
        };
      }
    }

    if (item.detectedType === 'excluded') {
      return {
        ...item,
        previewStatus: 'skipped',
        previewError: null,
        missingRequiredFields: [],
        detectedColumns: [],
        previewRows: [],
        productionPreview: null,
        directInputsPreview: null,
        importedManufacturingOrdersPreview: null,
        purchaseOrdersPreview: null,
      };
    }

    const candidateOrder = getCandidateOrderForFile(item);

    if (candidateOrder.length === 0) {
      return {
        ...item,
        previewStatus: 'idle',
        previewError: null,
        missingRequiredFields: [],
        detectedColumns: [],
        previewRows: [],
        productionPreview: null,
        directInputsPreview: null,
        importedManufacturingOrdersPreview: null,
        purchaseOrdersPreview: null,
      };
    }

    const analyzedCandidates: Array<{
      candidateKey: Exclude<ImportCenterDetectedType, 'unknown' | 'excluded'>;
      analyzed: ImportCenterFileItem;
    }> = [];

    let firstErrorResult: ImportCenterFileItem | null = null;

    for (const candidateKey of candidateOrder) {
      try {
        const analyzed = await analyzeFileItemByDetectedType({
          ...item,
          detectedType: candidateKey,
        });

        analyzedCandidates.push({
          candidateKey,
          analyzed,
        });

        if (analyzed.previewStatus === 'error' && !firstErrorResult) {
          firstErrorResult = analyzed;
        }
      } catch (error) {
        if (!firstErrorResult) {
          firstErrorResult = {
            ...item,
            previewStatus: 'error',
            previewError: getErrorMessage(
              error,
              'No se pudo generar el preview del archivo.',
            ),
            missingRequiredFields: [],
            detectedColumns: [],
            previewRows: [],
            productionPreview: null,
            directInputsPreview: null,
            importedManufacturingOrdersPreview: null,
        purchaseOrdersPreview: null,
            confirmSummary: null,
          };
        }
      }
    }

    const bestCandidate = analyzedCandidates
      .filter((entry) => entry.analyzed.previewStatus !== 'error')
      .sort((a, b) => {
        const statusPriorityDiff =
          getPreviewStatusPriority(b.analyzed.previewStatus) -
          getPreviewStatusPriority(a.analyzed.previewStatus);

        if (statusPriorityDiff !== 0) {
          return statusPriorityDiff;
        }

        const structuralScoreDiff =
          getAnalyzedCandidateStructuralScore(b.analyzed) -
          getAnalyzedCandidateStructuralScore(a.analyzed);

        if (structuralScoreDiff !== 0) {
          return structuralScoreDiff;
        }

        return 0;
      })[0];

    if (bestCandidate) {
      const usedFallback = bestCandidate.candidateKey !== item.detectedType;

      const nonErrorCandidatesCount = analyzedCandidates.filter(
        (entry) => entry.analyzed.previewStatus !== 'error',
      ).length;

      const structuralConfidence = getStructuralDetectionConfidence(
        bestCandidate.analyzed.previewStatus,
        nonErrorCandidatesCount,
      );

      const structuralReasonBase = buildStructuralDecisionReason(
        bestCandidate.candidateKey,
        bestCandidate.analyzed.previewStatus,
        nonErrorCandidatesCount,
        bestCandidate.analyzed,
      );

      const structuralReason =
        item.learnedSuggestedImporter === bestCandidate.candidateKey
          ? `${structuralReasonBase} ${
              item.learnedSuggestionSource === 'persisted'
                ? 'La prioridad inicial vino reforzada por una sugerencia aprendida en cargas anteriores.'
                : 'La prioridad inicial vino reforzada por aprendizaje dentro del lote.'
            }`
          : structuralReasonBase;

      return {
        ...bestCandidate.analyzed,
        detectedType: bestCandidate.candidateKey,
        autoReassignedByDetection: usedFallback,
        autoSelectedBestCandidate: nonErrorCandidatesCount > 1,
        detection: {
          ...item.detection,
          detectedType: bestCandidate.candidateKey,
          confidence: structuralConfidence,
          decisionReason: structuralReason,
        },
      };
    }

    if (item.detectedType === 'unknown') {
      if (firstErrorResult) {
        return {
          ...firstErrorResult,
          detectedType: 'unknown',
          detection: {
            ...item.detection,
            detectedType: 'unknown',
            confidence: 'none',
            decisionReason:
              'Se probaron los importadores disponibles, pero ninguno pudo analizar correctamente el archivo. Revisa el error mostrado antes de asignarlo manualmente.',
          },
        };
      }

      return {
        ...item,
        previewStatus: 'idle',
        previewError: null,
        missingRequiredFields: [],
        detectedColumns: [],
        previewRows: [],
        productionPreview: null,
        directInputsPreview: null,
        importedManufacturingOrdersPreview: null,
        purchaseOrdersPreview: null,
      };
    }

    return (
      firstErrorResult ?? {
        ...item,
        previewStatus: 'error',
        previewError: 'No se pudo generar el preview del archivo.',
        missingRequiredFields: [],
        detectedColumns: [],
        previewRows: [],
        productionPreview: null,
        directInputsPreview: null,
        importedManufacturingOrdersPreview: null,
        purchaseOrdersPreview: null,
        confirmSummary: null,
      }
    );
  }

  async function persistImportNormalizationProfile({
    importerKey,
    sheetName,
    headerRowIndex,
    mapping,
    knownColumns,
  }: {
    importerKey: string;
    sheetName: string | null | undefined;
    headerRowIndex: number | null | undefined;
    mapping: Record<string, string | null>;
    knownColumns: string[];
  }): Promise<void> {
    if (typeof headerRowIndex !== 'number') {
      return;
    }

    try {
      await commitFileNormalization({
        importer_key: importerKey,
        sheet_name: sheetName ?? null,
        header_row_index: headerRowIndex,
        data_start_row_index: headerRowIndex + 1,
        column_mapping: Object.fromEntries(
          Object.entries(mapping).filter(
            ([, value]) => value !== null && value !== '',
          ),
        ) as Record<string, string>,
        known_columns: knownColumns,
      });
    } catch {
      // La importación de datos ya se ha completado correctamente.
      // Un fallo al recordar el perfil no debe convertirla en fallida.
    }
  }

  async function confirmFileItem(item: ImportCenterFileItem): Promise<ImportCenterFileItem> {
    if (item.detectedType === 'production') {
      const baseFlow =
        item.productionFlow ??
        createProductionImportFlowState(item.file, item.productionConfig);
      // IND-053: un fichero con muchas menos filas que las que sustituye se confirma antes.
      const shrink = productionShrinkWarning(item.productionPreview, item.productionConfig.uploadMode);
      if (shrink && !window.confirm(shrink)) {
        throw new Error('No publicado: el fichero de producción trae muchas menos filas que las que sustituye.');
      }

      const confirmState = await confirmProductionImport({
        ...baseFlow,
        file: item.file,
        preview: item.productionPreview,
        mapping: item.mapping,
        productResolution: item.productResolution,
        config: item.productionConfig,
      });

      if (
        confirmState.status !== 'imported' &&
        confirmState.status !== 'imported_with_issues'
      ) {
        throw new Error(
          confirmState.error ||
            'No se pudo confirmar la importación de producción.',
        );
      }

      await persistImportNormalizationProfile({
        importerKey: 'production_volumes',
        sheetName: confirmState.normalizationSheetName,
        headerRowIndex: confirmState.normalizationHeaderRowIndex,
        mapping: confirmState.mapping,
        knownColumns: item.detectedColumns,
      });

      return {
        ...item,
        previewStatus:
          confirmState.status === 'imported_with_issues'
            ? 'imported_with_issues'
            : 'imported',
        previewError: confirmState.error,
        productionFlow: confirmState,
        productionConfig: confirmState.config,
        confirmSummary: confirmState.resultMessage,
      };
    }

    if (item.detectedType === 'direct-inputs') {
      const baseFlow =
        item.directInputsFlow ??
        createDirectInputsImportFlowState(item.file);

      const confirmState = await confirmDirectInputsImport({
        ...baseFlow,
        file: item.file,
        preview: item.directInputsPreview,
        mapping: item.mapping,
        materialResolution: item.materialResolution,
      });

      if (
        confirmState.status !== 'imported' &&
        confirmState.status !== 'imported_with_issues'
      ) {
        throw new Error(
          confirmState.error ||
            'No se pudo confirmar la importación de entradas.',
        );
      }

      await persistImportNormalizationProfile({
        importerKey: 'direct_material_entries',
        sheetName: confirmState.normalization?.sheet_name,
        headerRowIndex: confirmState.normalization?.header_row_index,
        mapping: confirmState.mapping,
        knownColumns: item.detectedColumns,
      });

      return {
        ...item,
        previewStatus:
          confirmState.status === 'imported_with_issues'
            ? 'imported_with_issues'
            : 'imported',
        previewError: confirmState.error,
        directInputsFlow: confirmState,
        confirmSummary: confirmState.resultMessage,
      };
    }

    if (item.detectedType === 'purchase-orders') {
      const baseFlow = item.purchaseOrdersFlow ??
        createPurchaseOrdersImportFlowState(item.file);
      const confirmState = await confirmPurchaseOrdersImport({
        ...baseFlow,
        file: item.file,
        preview: item.purchaseOrdersPreview,
        mapping: item.mapping,
      });

      if (
        confirmState.status !== 'imported' &&
        confirmState.status !== 'imported_with_issues'
      ) {
        throw new Error(
          confirmState.error || 'No se pudo confirmar la importación de Compras.',
        );
      }

      await persistImportNormalizationProfile({
        importerKey: 'purchase_orders_pending',
        sheetName: confirmState.normalization?.sheet_name,
        headerRowIndex: confirmState.normalization?.header_row_index,
        mapping: confirmState.mapping,
        knownColumns: item.detectedColumns,
      });

      return {
        ...item,
        previewStatus: confirmState.status,
        previewError: confirmState.error,
        purchaseOrdersFlow: confirmState,
        confirmSummary: confirmState.resultMessage,
      };
    }

    const baseFlow =
      item.importedManufacturingOrdersFlow ??
      createImportedManufacturingOrdersImportFlowState(item.file);

    const confirmState = await confirmImportedManufacturingOrdersImport({
      ...baseFlow,
      file: item.file,
      preview: item.importedManufacturingOrdersPreview,
      mapping: item.mapping,
      productResolution: item.productResolution,
    });

    if (
      confirmState.status !== 'imported' &&
      confirmState.status !== 'imported_with_issues'
    ) {
      throw new Error(
        confirmState.error ||
          'No se pudo confirmar la importación de pedidos pendientes.',
      );
    }

    await persistImportNormalizationProfile({
      importerKey: 'imported_manufacturing_orders',
      sheetName: confirmState.normalization?.sheet_name,
      headerRowIndex: confirmState.normalization?.header_row_index,
      mapping: confirmState.mapping,
      knownColumns: item.detectedColumns,
    });

    return {
      ...item,
      previewStatus: confirmState.status,
      previewError: confirmState.error,
      importedManufacturingOrdersFlow: confirmState,
      confirmSummary: confirmState.resultMessage,
    };
  }

  async function autoImportItemIfReady(
    item: ImportCenterFileItem,
    _successMessage: string,
    _errorMessage: string,
  ): Promise<ImportCenterFileItem> {
    // IC-DATA-11B1: los cambios de mapeo/configuración SOLO validan.
    // La única escritura de datos requiere Confirmar archivo / lote.
    return item;
  }

  async function reanalyzeFile(
    fileId: string,
    nextItem?: ImportCenterFileItem,
  ): Promise<ImportCenterFileItem | null> {
    const current = nextItem ?? files.find((item) => item.id === fileId);
    if (!current) return null;

    setPageError(null);
    setPageSuccess(null);

    setFiles((prev) =>
      prev.map((item) =>
        item.id === fileId
          ? {
              ...current,
              previewStatus:
                current.detectedType === 'excluded'
                  ? 'skipped'
                  : current.detectedType === 'unknown'
                    ? 'idle'
                    : 'loading',
              previewError: null,
            }
          : item,
      ),
    );

    const analyzed = await analyzeFileItem(current);

    setFiles((prev) =>
      prev.map((item) => (item.id === fileId ? analyzed : item)),
    );
    return analyzed;
  }

  async function reanalyzeSimilarPendingFiles(
    sourceItem: ImportCenterFileItem,
    candidatePool?: ImportCenterFileItem[],
  ) {
    if (!shouldUseItemForLearningPropagation(sourceItem)) {
      return;
    }

    const learnedImporter = sourceItem.detectedType as Exclude<
      ImportCenterDetectedType,
      'unknown' | 'excluded'
    >;

    const pool = candidatePool ?? files;

    const similarPendingFiles = pool
      .filter((item) => {
        if (item.id === sourceItem.id) {
          return false;
        }

        if (item.detectedType !== 'unknown') {
          return false;
        }

        if (!areFilesSimilarForImporterLearning(sourceItem, item)) {
          return false;
        }

        const similarityScore = getLearningCompatibilityScore(
          sourceItem,
          item,
        );

        return shouldReplaceLearnedSuggestion(
          item,
          learnedImporter,
          similarityScore,
        );
      })
      .sort(
        (a, b) =>
          getLearningCompatibilityScore(sourceItem, b) -
          getLearningCompatibilityScore(sourceItem, a),
      )
      .slice(0, 5);

    if (similarPendingFiles.length === 0) {
      return;
    }

    for (const similarFile of similarPendingFiles) {
      const similarityScore = getLearningCompatibilityScore(
        sourceItem,
        similarFile,
      );

      const suggestedItem: ImportCenterFileItem = {
        ...similarFile,
        detection: {
          ...similarFile.detection,
          detectedType: learnedImporter,
          confidence: 'low',
          decisionReason: `Importador sugerido automáticamente por aprendizaje intra-lote con ${sourceItem.file.name} (score ${similarityScore}).`,
        },
        detectedType: learnedImporter,
        autoReassignedByDetection: true,
        autoSelectedBestCandidate: false,
        learnedSuggestedImporter: learnedImporter,
        learnedSuggestionScore: similarityScore,
        learnedSuggestionSource: 'intra-lot',
        previewStatus: 'idle',
        previewError: null,
        confirmSummary: null,
      };

      await reanalyzeFile(similarFile.id, suggestedItem);
    }
  }

  // Fuente por nombre ya publicado (backend) o asignación aprendida de una
  // publicación confirmada en este navegador. No se codifican nombres de cliente.
  function getTrustedDailyImporter(
    item: ImportCenterFileItem,
  ): Exclude<ImportCenterDetectedType, 'unknown' | 'excluded'> | null {
    const basename = (value: string) =>
      value.replace(/\\/g, '/').split('/').pop()?.trim().toLowerCase() ?? '';
    const fileName = basename(item.file.name);
    const knownMatches = (sourceStatuses ?? [])
      .filter((status) => status.status !== 'not_found' &&
        status.source_file_name && basename(status.source_file_name) === fileName)
      .map((status) => status.source_key === 'manufacturing-orders'
        ? 'imported-manufacturing-orders' as const
        : status.source_key)
      .filter((key): key is Exclude<ImportCenterDetectedType, 'unknown' | 'excluded'> =>
        key === 'production' || key === 'direct-inputs' ||
        key === 'imported-manufacturing-orders' || key === 'purchase-orders');

    const distinctMatches = Array.from(new Set(knownMatches));
    const fromHistory = distinctMatches.length === 1 ? distinctMatches[0] : null;
    const fromLearning = item.learnedSuggestionSource === 'persisted' &&
      item.learnedSuggestedImporter &&
      (item.learnedSuggestionScore ?? 0) >= IMPORT_CENTER_PERSISTED_SUGGESTION_MIN_SCORE
        ? item.learnedSuggestedImporter
        : null;

    // Una contradicción entre fuentes aprendidas no se resuelve por puntuación.
    if (distinctMatches.length > 1 ||
        (fromHistory && fromLearning && fromHistory !== fromLearning)) {
      return null;
    }
    return fromHistory ?? fromLearning;
  }

  const handleFolderSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // El mismo análisis funciona con una carpeta o varios archivos elegidos.
    const selectedFiles = Array.from(event.target.files ?? []);
    // IND-002: se ignoran los temporales de Excel (~$…) y lo que esté en
    // subcarpetas; solo cuentan los ficheros de la carpeta elegida.
    const isInSubfolder = (file: File) =>
      (file.webkitRelativePath || '').split('/').filter(Boolean).length > 2;
    const baseFiles = selectedFiles
      .filter((file) => isAcceptedImportCenterFile(file.name))
      .filter((file) => !file.name.startsWith('~$'))
      .filter((file) => !isInSubfolder(file))
      .map((file, index) => buildBaseFileItem(file, index));

    type DailyKey = Exclude<ImportCenterDetectedType, 'unknown' | 'excluded'>;
    const initialAssignments = baseFiles.map((item) => ({
      item,
      importer: getTrustedDailyImporter(item) as DailyKey | null,
    }));
    const usedKeys = initialAssignments.map((entry) => entry.importer);
    const assignments = initialAssignments.map((entry) => ({
      ...entry,
      // Dos archivos que reclamen la misma fuente requieren revisión.
      importer: entry.importer && usedKeys.filter((key) => key === entry.importer).length === 1
        ? entry.importer
        : null,
    }));
    const unmatched = assignments.filter((entry) => !entry.importer);
    const assignedKeys = new Set(assignments.map((entry) => entry.importer).filter(Boolean));
    const remaining = getImportCenterVisibleImporters()
      .map((importer) => importer.key)
      .filter((key) => !assignedKeys.has(key));

    // Con tres identidades verificadas y cuatro archivos, el cuarto ocupa la
    // fuente libre, pero DEBE superar el preview de su propio importador.
    if (baseFiles.length === 4 && unmatched.length === 1 && remaining.length === 1) {
      unmatched[0].importer = remaining[0];
    }

    const preparedFiles = assignments.map(({ item, importer }) => importer
      ? {
          ...item,
          detectedType: importer,
          lockedDailyImporter: true,
          detection: {
            ...item.detection,
            detectedType: importer,
            confidence: 'high' as const,
            decisionReason: 'Asignación de fuente diaria recuperada del historial o del lote completo. Validar con el importador específico.',
          },
        }
      : {
          ...item,
          detectedType: 'unknown' as const,
          lockedDailyImporter: false,
          previewStatus: 'idle' as const,
          detection: {
            ...item.detection,
            detectedType: 'unknown' as const,
            confidence: 'low' as const,
            decisionReason: 'No existe correspondencia inequívoca con una fuente diaria. Asignar una vez manualmente.',
          },
        });

    event.target.value = '';
    setPageError(null);
    setPageSuccess(null);
    setFiles(preparedFiles);
    setActiveFileId(preparedFiles[0]?.id ?? null);

    if (baseFiles.length === 0) {
      setPageError('No se han encontrado archivos Excel o CSV compatibles.');
      return;
    }

    setLoadingFolder(true);
    try {
      // IND-019: en la carga por carpeta los productos nuevos no bloquean
      // (el backend los da de alta al confirmar); sin homologación.
      const acceptNewProducts = (item: ImportCenterFileItem): ImportCenterFileItem =>
        compact && item.detectedType === 'production' && item.previewStatus === 'needs_resolution'
          ? { ...item, previewStatus: 'ready' }
          : item;
      const firstPass = await Promise.all(
        preparedFiles.map((item) => item.lockedDailyImporter
          ? analyzeFileItem(item).then(acceptNewProducts)
          : Promise.resolve(item)),
      );

      // IND-002: un fichero sin fuente reconocida por su nombre se reconoce
      // por sus columnas (p. ej. «lplanifpol2automatico (1).xlsx»). Solo se
      // acepta si su importador lo valida y esa fuente no está ya ocupada.
      const takenKeys = new Set(
        firstPass.filter((item) => item.lockedDailyImporter).map((item) => item.detectedType),
      );
      const analyzed: ImportCenterFileItem[] = [];
      for (const item of firstPass) {
        if (item.lockedDailyImporter) {
          analyzed.push(item);
          continue;
        }
        const byColumns = acceptNewProducts(await analyzeFileItem(item));
        if (
          byColumns.previewStatus === 'ready' &&
          byColumns.detectedType !== 'unknown' &&
          byColumns.detectedType !== 'excluded' &&
          !takenKeys.has(byColumns.detectedType)
        ) {
          takenKeys.add(byColumns.detectedType);
          analyzed.push({
            ...byColumns,
            lockedDailyImporter: true,
            detection: {
              ...byColumns.detection,
              decisionReason: 'Fuente reconocida por las columnas del fichero.',
            },
          });
        } else if (byColumns.previewStatus === 'ready') {
          // Otra copia de una fuente ya ocupada: no se publica y no frena a
          // los demás ficheros.
          analyzed.push({
            ...byColumns,
            previewStatus: 'skipped',
            previewError: 'Otra copia de una fuente que ya tiene fichero en esta carpeta; no se carga.',
          });
        } else {
          analyzed.push(byColumns);
        }
      }
      setFiles(analyzed);
      setActiveFileId(getPreferredActiveFileId(analyzed));

      // IND-002: cada fichero va por su cuenta. Se publican los reconocidos y
      // listos; uno con problemas no frena a los demás.
      const publishable = analyzed.filter((item) =>
        item.lockedDailyImporter && item.previewStatus === 'ready',
      );
      // IND-019: en la carga diaria se guarda siempre el resultado, aunque
      // ningún fichero se pueda publicar.
      const autoPublish = compact;

      if (autoPublish) {
        // Cambiar de «Analizando» a «Actualizando» sin segundo clic.
        setLoadingFolder(false);
        await handleConfirmReadyBatch(analyzed, publishable);
      } else {
        setPageSuccess(
          `Análisis finalizado: ${analyzed.length} archivo(s), ` +
          `${analyzed.filter((item) => item.previewStatus === 'ready').length} listos. ` +
          'No se ha publicado ningún dato. Revisa las fuentes pendientes.',
        );
      }
    } catch (error) {
      setPageError(getErrorMessage(error, 'No se pudo analizar el conjunto de archivos.'));
    } finally {
      setLoadingFolder(false);
    }
  };

  const handleChangeDetectedType = async (
    fileId: string,
    nextType: ImportCenterDetectedType,
  ) => {
    const current = files.find((item) => item.id === fileId);
    if (!current) return;

    const updated: ImportCenterFileItem = {
      ...current,
      detection: {
        ...current.detection,
        detectedType: nextType,
        confidence:
          nextType === 'unknown'
            ? 'low'
            : nextType === 'excluded'
              ? 'medium'
              : 'high',
        decisionReason:
          nextType === 'excluded'
            ? 'Archivo excluido manualmente del lote por decisión del usuario.'
            : nextType === 'unknown'
              ? 'Archivo marcado manualmente como pendiente de clasificación.'
              : `Importador asignado manualmente por el usuario: ${getDetectedTypeLabel(nextType)}.`,
      },
      detectedType: nextType,
      autoReassignedByDetection: false,
      autoSelectedBestCandidate: false,
      learnedSuggestedImporter: null,
      learnedSuggestionScore: null,
      learnedSuggestionSource: null,
      lockedDailyImporter: nextType !== 'unknown' && nextType !== 'excluded',
      productionPreview: null,
      directInputsPreview: null,
      purchaseOrdersPreview: null,
      purchaseOrdersFlow: null,
      previewRows: [],
      detectedColumns: [],
      missingRequiredFields: [],
      previewError: null,
      confirmSummary: null,
      previewStatus: nextType === 'excluded' ? 'skipped' : 'idle',
    };

    const finalUpdatedItem = await reanalyzeFile(fileId, updated);

    if (finalUpdatedItem && canPropagateLearningFromItem(finalUpdatedItem)) {
      await reanalyzeSimilarPendingFiles(finalUpdatedItem);
    }
  };

  const handleChangeProductionConfig = async (
    field: keyof ImportCenterProductionConfig,
    value: string,
  ) => {
    if (!activeFile) return;
    if (activeFile.detectedType !== 'production') return;

    const baseFlow =
      activeFile.productionFlow ??
      createProductionImportFlowState(activeFile.file, activeFile.productionConfig);

    setFiles((prev) =>
      prev.map((item) =>
        item.id === activeFile.id
          ? {
              ...item,
              previewStatus: 'loading',
              previewError: null,
            }
          : item,
      ),
    );

    const nextFlow = await applyProductionImportConfig(baseFlow, {
      [field]: value,
    });

    let nextStatus: ImportCenterPreviewStatus = 'ready';

    if (nextFlow.status === 'needs_mapping') {
      nextStatus = 'needs_mapping';
    } else if (nextFlow.status === 'needs_resolution') {
      nextStatus = 'needs_resolution';
    } else if (nextFlow.status === 'error') {
      nextStatus = 'error';
    } else {
      const unresolvedRefs = getProductionUnresolvedProductRefs(nextFlow);
      nextStatus = unresolvedRefs.length > 0 ? 'needs_resolution' : 'ready';
    }

    const updatedItem: ImportCenterFileItem = {
      ...activeFile,
      previewStatus: nextStatus,
      previewError: nextFlow.error,
      missingRequiredFields: nextFlow.preview?.missing_required_fields ?? [],
      detectedColumns: nextFlow.preview?.columns ?? [],
      previewRows: (nextFlow.preview?.preview_rows ?? []) as Array<
        Record<string, unknown>
      >,
      mapping: nextFlow.mapping,
      productResolution: nextFlow.productResolution,
      productionConfig: nextFlow.config,
      productionPreview: nextFlow.preview,
      productionFlow: nextFlow,
      confirmSummary: null,
    };

    setFiles((prev) =>
      prev.map((item) => (item.id === activeFile.id ? updatedItem : item)),
    );

    await autoImportItemIfReady(
      updatedItem,
      `Archivo importado automáticamente tras actualizar la configuración: ${updatedItem.file.name}`,
      `No se pudo auto-importar el archivo tras actualizar la configuración ${updatedItem.file.name}.`,
    );
  };

  const handleChangeMapping = async (field: string, value: string) => {
    if (!activeFile) return;

    // El flujo de Compras ya resuelve el mapeo en su servicio de preview.
    if (activeFile.detectedType === 'purchase-orders') {
      const nextMapping = {
        ...activeFile.mapping,
        [field]: value || null,
      };
      await reanalyzeFile(activeFile.id, {
        ...activeFile,
        mapping: nextMapping,
        purchaseOrdersFlow: activeFile.purchaseOrdersFlow
          ? { ...activeFile.purchaseOrdersFlow, mapping: nextMapping }
          : null,
      });
      return;
    }

    if (activeFile.detectedType === 'direct-inputs') {
      const baseFlow =
        activeFile.directInputsFlow ??
        createDirectInputsImportFlowState(activeFile.file);

      setFiles((prev) =>
        prev.map((item) =>
          item.id === activeFile.id
            ? {
                ...item,
                previewStatus: 'loading',
                previewError: null,
              }
            : item,
        ),
      );

      const nextFlow = await applyDirectInputsImportMapping(baseFlow, field, value);

      const flowSummary = getDirectInputsImportSummary(nextFlow);

      let nextStatus: ImportCenterPreviewStatus = 'ready';

      if (nextFlow.status === 'needs_mapping') {
        nextStatus = 'needs_mapping';
      } else if (nextFlow.status === 'error') {
        nextStatus = 'error';
      } else if (!flowSummary.canContinue) {
        nextStatus = 'needs_mapping';
      } else {
        nextStatus = 'ready';
      }

      const updatedItem: ImportCenterFileItem = {
        ...activeFile,
        previewStatus: nextStatus,
        previewError: nextFlow.error,
        missingRequiredFields: nextFlow.preview?.missing_required_fields ?? [],
        detectedColumns: nextFlow.preview?.columns ?? [],
        previewRows: (nextFlow.preview?.preview_rows ?? []) as Array<
          Record<string, unknown>
        >,
        mapping: nextFlow.mapping,
        materialResolution: nextFlow.materialResolution,
        directInputsPreview: nextFlow.preview,
        directInputsFlow: nextFlow,
        confirmSummary: null,
      };

      setFiles((prev) =>
        prev.map((item) => (item.id === activeFile.id ? updatedItem : item)),
      );

      await autoImportItemIfReady(
        updatedItem,
        `Archivo importado automáticamente tras actualizar el mapeo: ${updatedItem.file.name}`,
        `No se pudo auto-importar el archivo tras actualizar el mapeo ${updatedItem.file.name}.`,
      );

      return;
    }

    if (activeFile.detectedType === 'production') {
      const baseFlow =
        activeFile.productionFlow ??
        createProductionImportFlowState(activeFile.file, activeFile.productionConfig);

      setFiles((prev) =>
        prev.map((item) =>
          item.id === activeFile.id
            ? {
                ...item,
                previewStatus: 'loading',
                previewError: null,
              }
            : item,
        ),
      );

      const nextFlow = await applyProductionImportMapping(baseFlow, field, value);

      let nextStatus: ImportCenterPreviewStatus = 'ready';

      if (nextFlow.status === 'needs_mapping') {
        nextStatus = 'needs_mapping';
      } else if (nextFlow.status === 'needs_resolution') {
        nextStatus = 'needs_resolution';
      } else if (nextFlow.status === 'error') {
        nextStatus = 'error';
      } else {
        const unresolvedRefs = getProductionUnresolvedProductRefs(nextFlow);
        nextStatus = unresolvedRefs.length > 0 ? 'needs_resolution' : 'ready';
      }

      const updatedItem: ImportCenterFileItem = {
        ...activeFile,
        previewStatus: nextStatus,
        previewError: nextFlow.error,
        missingRequiredFields: nextFlow.preview?.missing_required_fields ?? [],
        detectedColumns: nextFlow.preview?.columns ?? [],
        previewRows: (nextFlow.preview?.preview_rows ?? []) as Array<
          Record<string, unknown>
        >,
        mapping: nextFlow.mapping,
        productResolution: nextFlow.productResolution,
        productionConfig: nextFlow.config,
        productionPreview: nextFlow.preview,
        productionFlow: nextFlow,
        confirmSummary: null,
      };

      setFiles((prev) =>
        prev.map((item) => (item.id === activeFile.id ? updatedItem : item)),
      );

      await autoImportItemIfReady(
        updatedItem,
        `Archivo importado automáticamente tras actualizar la configuración: ${updatedItem.file.name}`,
        `No se pudo auto-importar el archivo tras actualizar la configuración ${updatedItem.file.name}.`,
      );

      return;
    }

    if (activeFile.detectedType === 'imported-manufacturing-orders') {
      const baseFlow =
        activeFile.importedManufacturingOrdersFlow ??
        createImportedManufacturingOrdersImportFlowState(activeFile.file);

      setFiles((prev) =>
        prev.map((item) =>
          item.id === activeFile.id
            ? {
                ...item,
                previewStatus: 'loading',
                previewError: null,
              }
            : item,
        ),
      );

      const nextFlow = await applyImportedManufacturingOrdersImportMapping(
        baseFlow,
        field,
        value,
      );

      const flowSummary = getImportedManufacturingOrdersImportSummary(nextFlow);

      let nextStatus: ImportCenterPreviewStatus = 'ready';

      if (nextFlow.status === 'needs_mapping') {
        nextStatus = 'needs_mapping';
      } else if (nextFlow.status === 'error') {
        nextStatus = 'error';
      } else if (nextFlow.status === 'idle') {
        nextStatus = 'idle';
      } else if (!flowSummary.canContinue) {
        nextStatus = 'needs_mapping';
      } else {
        nextStatus = 'ready';
      }

      const updatedItem: ImportCenterFileItem = {
        ...activeFile,
        previewStatus: nextStatus,
        previewError: nextFlow.error,
        missingRequiredFields: nextFlow.preview?.missing_required_fields ?? [],
        detectedColumns: nextFlow.preview?.columns ?? [],
        previewRows: (nextFlow.preview?.preview_rows ?? []) as Array<
          Record<string, unknown>
        >,
        mapping: nextFlow.mapping,
        importedManufacturingOrdersPreview: nextFlow.preview,
        importedManufacturingOrdersFlow: nextFlow,
        confirmSummary: null,
      };

      setFiles((prev) =>
        prev.map((item) => (item.id === activeFile.id ? updatedItem : item)),
      );

      await autoImportItemIfReady(
        updatedItem,
        `Archivo importado automáticamente tras actualizar el mapeo: ${updatedItem.file.name}`,
        `No se pudo auto-importar el archivo tras actualizar el mapeo ${updatedItem.file.name}.`,
      );

      return;
    }

    const updated: ImportCenterFileItem = {
      ...activeFile,
      mapping: {
        ...activeFile.mapping,
        [field]: value || null,
      },
    };

    await reanalyzeFile(activeFile.id, updated);
  };

  const handleChangeProductResolution = async (
    sourceValue: string,
    productId: string,
  ) => {
    if (!activeFile) return;

    setFiles((prev) =>
      prev.map((item) =>
        item.id === activeFile.id
          ? {
              ...item,
              previewStatus: 'loading',
              previewError: null,
            }
          : item,
      ),
    );

    if (
      activeFile.detectedType ===
      'imported-manufacturing-orders'
    ) {
      const baseFlow =
        activeFile.importedManufacturingOrdersFlow ??
        createImportedManufacturingOrdersImportFlowState(
          activeFile.file,
        );

      const nextFlow =
        await applyImportedManufacturingOrdersProductResolution(
          baseFlow,
          sourceValue,
          productId,
        );

      const unresolvedRefs =
        getImportedManufacturingOrdersUnresolvedProductRefs(
          nextFlow,
        );

      let nextStatus: ImportCenterPreviewStatus = 'ready';

      if (nextFlow.status === 'needs_mapping') {
        nextStatus = 'needs_mapping';
      } else if (nextFlow.status === 'error') {
        nextStatus = 'error';
      } else {
        nextStatus =
          unresolvedRefs.length > 0
            ? 'needs_resolution'
            : 'ready';
      }

      const updatedItem: ImportCenterFileItem = {
        ...activeFile,
        previewStatus: nextStatus,
        previewError: nextFlow.error,
        missingRequiredFields:
          nextFlow.preview?.missing_required_fields ?? [],
        detectedColumns:
          nextFlow.preview?.columns ?? [],
        previewRows: (
          nextFlow.preview?.preview_rows ?? []
        ) as Array<Record<string, unknown>>,
        mapping: nextFlow.mapping,
        productResolution: nextFlow.productResolution,
        importedManufacturingOrdersPreview:
          nextFlow.preview,
        importedManufacturingOrdersFlow: nextFlow,
        confirmSummary: null,
      };

      setFiles((prev) =>
        prev.map((item) =>
          item.id === activeFile.id
            ? updatedItem
            : item,
        ),
      );

      await autoImportItemIfReady(
        updatedItem,
        `Archivo importado automáticamente tras resolver la homologación: ${updatedItem.file.name}`,
        `No se pudo auto-importar el archivo tras resolver la homologación ${updatedItem.file.name}.`,
      );

      return;
    }

    if (activeFile.detectedType !== 'production') {
      return;
    }

    const baseFlow =
      activeFile.productionFlow ??
      createProductionImportFlowState(
        activeFile.file,
        activeFile.productionConfig,
      );

    const nextFlow =
      await applyProductionProductResolution(
        baseFlow,
        sourceValue,
        productId,
      );

    let nextStatus: ImportCenterPreviewStatus = 'ready';

    if (nextFlow.status === 'needs_mapping') {
      nextStatus = 'needs_mapping';
    } else if (nextFlow.status === 'needs_resolution') {
      nextStatus = 'needs_resolution';
    } else if (nextFlow.status === 'error') {
      nextStatus = 'error';
    } else {
      const unresolvedRefs =
        getProductionUnresolvedProductRefs(nextFlow);

      nextStatus =
        unresolvedRefs.length > 0
          ? 'needs_resolution'
          : 'ready';
    }

    const updatedItem: ImportCenterFileItem = {
      ...activeFile,
      previewStatus: nextStatus,
      previewError: nextFlow.error,
      missingRequiredFields:
        nextFlow.preview?.missing_required_fields ?? [],
      detectedColumns: nextFlow.preview?.columns ?? [],
      previewRows: (
        nextFlow.preview?.preview_rows ?? []
      ) as Array<Record<string, unknown>>,
      mapping: nextFlow.mapping,
      productResolution: nextFlow.productResolution,
      productionConfig: nextFlow.config,
      productionPreview: nextFlow.preview,
      productionFlow: nextFlow,
      confirmSummary: null,
    };

    setFiles((prev) =>
      prev.map((item) =>
        item.id === activeFile.id
          ? updatedItem
          : item,
      ),
    );

    await autoImportItemIfReady(
      updatedItem,
      `Archivo importado automáticamente tras resolver la homologación: ${updatedItem.file.name}`,
      `No se pudo auto-importar el archivo tras resolver la homologación ${updatedItem.file.name}.`,
    );
  };

  const handleChangeMaterialResolution = async (
    sourceValue: string,
    materialId: string,
  ) => {
    if (!activeFile) return;

    const baseFlow =
      activeFile.directInputsFlow ??
      createDirectInputsImportFlowState(activeFile.file);

    setFiles((prev) =>
      prev.map((item) =>
        item.id === activeFile.id
          ? {
              ...item,
              previewStatus: 'loading',
              previewError: null,
            }
          : item,
      ),
    );

    const nextFlow = await applyDirectInputsMaterialResolution(
      baseFlow,
      sourceValue,
      materialId,
    );

    const flowSummary = getDirectInputsImportSummary(nextFlow);

    let nextStatus: ImportCenterPreviewStatus = 'ready';

    if (nextFlow.status === 'needs_mapping') {
      nextStatus = 'needs_mapping';
    } else if (nextFlow.status === 'error') {
      nextStatus = 'error';
    } else if (!flowSummary.canContinue) {
      nextStatus = 'needs_mapping';
    } else {
      nextStatus = 'ready';
    }

    const updatedItem: ImportCenterFileItem = {
      ...activeFile,
      previewStatus: nextStatus,
      previewError: nextFlow.error,
      missingRequiredFields: nextFlow.preview?.missing_required_fields ?? [],
      detectedColumns: nextFlow.preview?.columns ?? [],
      previewRows: (nextFlow.preview?.preview_rows ?? []) as Array<
        Record<string, unknown>
      >,
      mapping: nextFlow.mapping,
      materialResolution: nextFlow.materialResolution,
      directInputsPreview: nextFlow.preview,
      directInputsFlow: nextFlow,
      confirmSummary: null,
    };

    setFiles((prev) =>
      prev.map((item) => (item.id === activeFile.id ? updatedItem : item)),
    );

    await autoImportItemIfReady(
      updatedItem,
      `Archivo importado automáticamente tras resolver la homologación: ${updatedItem.file.name}`,
      `No se pudo auto-importar el archivo tras resolver la homologación ${updatedItem.file.name}.`,
    );
  };

  const handleConfirmActiveFile = async () => {
    if (!activeFile) return;
    if (activeFile.previewStatus !== 'ready') return;

    setPageError(null);
    setPageSuccess(null);

    setFiles((prev) =>
      prev.map((item) =>
        item.id === activeFile.id ? { ...item, previewStatus: 'importing' } : item,
      ),
    );

    try {
      const confirmed = await confirmFileItem(activeFile);
      rememberImporterAfterConfirmedPublication(confirmed);
      setFiles((prev) =>
        prev.map((item) => (item.id === activeFile.id ? confirmed : item)),
      );
      setPageSuccess(`Archivo confirmado: ${activeFile.file.name}`);
      onImported?.();
    } catch (error) {
      setFiles((prev) =>
        prev.map((item) =>
          item.id === activeFile.id
            ? {
                ...item,
                previewStatus: 'error',
                previewError: getErrorMessage(
                  error,
                  'No se pudo confirmar la importación del archivo.',
                ),
              }
            : item,
        ),
      );
      setPageError(
        getErrorMessage(
          error,
          `No se pudo confirmar el archivo ${activeFile.file.name}.`,
        ),
      );
    }
  };

  const handleConfirmReadyBatch = async (
    batchFiles?: ImportCenterFileItem[],
    onlyFiles?: ImportCenterFileItem[],
  ) => {
    // Usar el resultado analizado directamente: no depender del estado React
    // que aún podría estar pendiente de aplicar tras seleccionar una carpeta.
    const currentFiles = batchFiles ?? files;
    // IND-002: orden fijo de publicación: albaranes y compras; después
    // producción y OF.
    const publicationOrder: ImportCenterDetectedType[] = [
      'direct-inputs',
      'purchase-orders',
      'production',
      'imported-manufacturing-orders',
    ];
    const orderOf = (item: ImportCenterFileItem) => {
      const position = publicationOrder.indexOf(item.detectedType);
      return position < 0 ? publicationOrder.length : position;
    };
    const onlyIds = onlyFiles ? new Set(onlyFiles.map((item) => item.id)) : null;
    const readyFiles = currentFiles
      .filter((item) => item.previewStatus === 'ready')
      .filter((item) => !onlyIds || onlyIds.has(item.id))
      .sort((a, b) => orderOf(a) - orderOf(b));
    // IND-019: la carga diaria sigue aunque no haya nada listo, para guardar
    // y enseñar el resultado de cada fichero.
    if (readyFiles.length === 0 && !(compact && onlyFiles)) {
      return;
    }
    const readyKeys = readyFiles.map((item) => item.detectedType);
    if (readyKeys.some((key, index) => readyKeys.indexOf(key) !== index)) {
      setPageError('Hay varios archivos listos para una misma fuente. Revisa el lote y confirma cada uno por separado.');
      return;
    }

    setConfirmingBatch(true);
    setPageError(null);
    setPageSuccess(null);

    setFiles((prev) =>
      prev.map((item) =>
        item.previewStatus === 'ready'
          ? { ...item, previewStatus: 'importing', previewError: null }
          : item,
      ),
    );

    let publishedCount = 0;

    const updatedFiles = [...currentFiles];

    for (const readyFile of readyFiles) {
      try {
        const confirmed = await confirmFileItem(readyFile);
        rememberImporterAfterConfirmedPublication(confirmed);
        const index = updatedFiles.findIndex((item) => item.id === readyFile.id);
        if (index >= 0) {
          updatedFiles[index] = confirmed;
        }

        publishedCount += 1;
      } catch (error) {
        const index = updatedFiles.findIndex((item) => item.id === readyFile.id);
        if (index >= 0) {
          updatedFiles[index] = {
            ...updatedFiles[index],
            previewStatus: 'error',
            previewError: getErrorMessage(
              error,
              'No se pudo confirmar la importación del archivo.',
            ),
          };
        }
      }

      setFiles([...updatedFiles]);
    }

    setConfirmingBatch(false);
    // IND-017: el mensaje cuenta igual que el resumen guardado: toda fuente
    // que no se publicó (error, revisión, mapeo…) cuenta como no publicada.
    const sourceFiles = updatedFiles.filter(
      (item) => item.detectedType !== 'unknown' && item.detectedType !== 'excluded',
    );
    // IND-004: solo las cuatro fuentes; las que no estaban, «No cargado».
    const runFiles: DailyLoadRunFile[] = [
      ...sourceFiles.map(getDailyRunFile),
      ...missingDailySources(sourceFiles.map((item) => item.detectedType)),
    ].slice(0, 50);

    // IND-002: guardar el resultado por fichero para verlo tras recargar.
    if (compact) {
      try {
        setLastDailyRun(await saveDailyLoadRun(runFiles));
      } catch {
        // Guardar el resumen no debe ocultar el resultado de la carga.
      }
      // IND-019: la línea de avisos pendientes se vuelve a pedir tras guardar.
      void queryClient.invalidateQueries({ queryKey: PLANT_DATA_REVIEW_QUERY_KEY });
    }
    // IND-004: tras guardar el resumen, para que «Revisa tus ficheros» y
    // «Datos a fecha» ya lo incluyan.
    if (publishedCount > 0) {
      onImported?.();
    }

    // IND-019: «Carga completa: 4 de 4 ficheros actualizados» o «Carga
    // incompleta: N fichero(s) sin publicar».
    setPageSuccess(compact
      ? dailyRunMessage(runFiles)
      : `Carga completada: ${publishedCount} archivo(s) publicados.`);
  };

  const unresolvedProductRefs = useMemo(() => {
    if (!activeFile) return [];

    if (
      activeFile.detectedType === 'production' &&
      activeFile.productionFlow
    ) {
      return getProductionUnresolvedProductRefs(
        activeFile.productionFlow,
      );
    }

    if (
      activeFile.detectedType ===
        'imported-manufacturing-orders' &&
      activeFile.importedManufacturingOrdersFlow
    ) {
      return getImportedManufacturingOrdersUnresolvedProductRefs(
        activeFile.importedManufacturingOrdersFlow,
      );
    }

    return [];
  }, [activeFile]);

  const sortedDetectedProductRefs = useMemo(() => {
    if (!activeFile) return [];

    const detectedRefs =
      activeFile.detectedType === 'production'
        ? activeFile.productionPreview?.detected_product_refs
        : activeFile.detectedType ===
            'imported-manufacturing-orders'
          ? activeFile.importedManufacturingOrdersPreview
              ?.detected_product_refs
          : null;

    if (!detectedRefs?.length) {
      return [];
    }

    return sortResolutionItems(
      detectedRefs,
      (ref) => {
        const resolvedValue =
          activeFile.productResolution[ref.source_value] ??
          ref.resolved_product_id ??
          '';

        return Boolean(resolvedValue);
      },
    );
  }, [activeFile]);

  const unresolvedMaterialRefs = useMemo(() => {
    if (!activeFile?.directInputsFlow) return [];

    return getDirectInputsUnresolvedMaterialRefs(activeFile.directInputsFlow);
  }, [activeFile]);

  const sortedDetectedMaterialRefs = useMemo(() => {
    if (!activeFile?.directInputsPreview?.detected_material_refs.length) return [];

    return sortResolutionItems(
      activeFile.directInputsPreview.detected_material_refs,
      (ref) => {
        const resolvedValue =
          activeFile.materialResolution[ref.source_value] ??
          ref.resolved_material_id ??
          '';

        return Boolean(resolvedValue);
      },
    );
  }, [activeFile]);

  const activePurchaseOrdersSummary = useMemo(() => {
    if (!activeFile?.purchaseOrdersFlow || activeFile.detectedType !== 'purchase-orders') {
      return null;
    }
    return getPurchaseOrdersImportSummary(activeFile.purchaseOrdersFlow);
  }, [activeFile]);

  const activeDirectInputsSummary = useMemo(() => {
    if (!activeFile?.directInputsFlow) return null;

    return getDirectInputsImportSummary(activeFile.directInputsFlow);
  }, [activeFile]);

  const sortedMappingEntries = useMemo(() => {
    if (!activeFile) return [];

    return getSortedMappingEntries(
      activeFile.mapping,
      activeFile.missingRequiredFields,
    );
  }, [activeFile]);

  const canConfirmActiveFile = Boolean(
    activeFile &&
      (
        activeFile.previewStatus === 'ready' ||
        (
          activeFile.detectedType === 'direct-inputs' &&
          activeDirectInputsSummary?.canContinue
        )
      ),
  );
  const readyItems = files.filter((item) => item.previewStatus === 'ready');
  const readyImporterKeys = readyItems.map((item) => item.detectedType);
  const duplicateReadyImporter = readyImporterKeys.some(
    (key, index) => readyImporterKeys.indexOf(key) !== index,
  );
  // IND-002: los albaranes ya no bloquean la carga de los demás ficheros.
  const canConfirmBatch = readyItems.length > 0 && !duplicateReadyImporter;

  if (compact && !showAdvanced) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.75 },
          borderRadius: 3,
          bgcolor: 'rgba(15,23,42,0.72)',
          border: '1px solid rgba(148,163,184,0.20)',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".xlsx,.xls,.csv"
          // @ts-expect-error webkitdirectory no está declarado en los tipos de React.
          webkitdirectory=""
          style={{ display: 'none' }}
          onChange={handleFolderSelected}
        />

        <Stack spacing={2.25}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              <Typography sx={{ color: '#f8fafc', fontSize: 19, fontWeight: 900 }}>
                Actualización diaria
              </Typography>
              <Typography sx={{ color: '#94a3b8', mt: 0.5, fontSize: 13 }}>
                Selecciona una carpeta: PROA valida y carga las cuatro fuentes automáticamente.
              </Typography>
              {!canImport ? (
                <Typography sx={{ color: '#fbbf24', mt: 0.5, fontSize: 12 }}>
                  {IMPORT_PERMISSION_DENIED_TEXT}
                </Typography>
              ) : null}
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="contained"
                onClick={() => inputRef.current?.click()}
                disabled={loadingFolder || confirmingBatch || !canImport}
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                Seleccionar carpeta del día
              </Button>
            </Stack>
          </Stack>

          {/* IND-002: resultado de la última carga diaria, guardado en el servidor.
              IND-019: las tarjetas enseñan cada fichero; aquí, el mensaje general. */}
          {files.length === 0 && !loadingFolder && !confirmingBatch && lastDailyRun ? (
            <Stack spacing={0.4}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 800, fontSize: 14 }}>
                Última carga ·{' '}
                {new Intl.DateTimeFormat('es-ES', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                }).format(new Date(lastDailyRun.created_at))}
                {' · '}
                {dailyRunMessage(lastDailyRun.files)}
              </Typography>
              <PendingNoticesLine />
            </Stack>
          ) : null}

          {loadingFolder || confirmingBatch ? (
            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 2.5, bgcolor: 'rgba(2,6,23,0.35)' }}
            >
              <Stack spacing={1.5}>
                <Typography sx={{ fontWeight: 800, color: '#f8fafc' }}>
                  {confirmingBatch ? 'Actualizando las fuentes...' : 'Analizando los archivos...'}
                </Typography>
                <LinearProgress />
                <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
                  {confirmingBatch
                    ? 'Los archivos se están publicando automáticamente tras superar las validaciones.'
                    : 'Se comprueban las cuatro fuentes y sus mapeos antes de actualizar.'}
                </Typography>
              </Stack>
            </Paper>
          ) : (
            <>
              {pageError ? <Alert severity="error">{pageError}</Alert> : null}
              {pageSuccess && files.length > 0 ? (
                <Stack spacing={0.4}>
                  <Typography sx={{ fontSize: 13, color: '#cbd5e1', fontWeight: 700 }}>
                    {pageSuccess}
                  </Typography>
                  {compact ? <PendingNoticesLine /> : null}
                </Stack>
              ) : files.length === 0 && lastDailyRun && compact ? null : files.length === 0 && !sourceStatusesLoading && !sourceStatusesError && sourceStatuses ? (
                <Typography sx={{ fontSize: 13, color: '#cbd5e1' }}>
                  {`${['production', 'direct-inputs', 'manufacturing-orders', 'purchase-orders'].filter(
                    (key) => sourceStatuses.some((status) => status.source_key === key && status.status !== 'not_found'),
                  ).length} de 4 fuentes con datos. Selecciona una carpeta para actualizarlas.`}
                </Typography>
              ) : null}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                {getImportCenterVisibleImporters()
                  .sort((a, b) => [
                    'production', 'direct-inputs', 'imported-manufacturing-orders', 'purchase-orders',
                  ].indexOf(a.key) - [
                    'production', 'direct-inputs', 'imported-manufacturing-orders', 'purchase-orders',
                  ].indexOf(b.key))
                  .map((importer) => {
                    const matched = files.filter((item) => item.detectedType === importer.key);
                    const selected = matched[0];
                    const statusKey = importer.key === 'imported-manufacturing-orders'
                      ? 'manufacturing-orders'
                      : importer.key;
                    const previous = sourceStatuses?.find((entry) => entry.source_key === statusKey);
                    const hasPriorData = Boolean(previous && previous.status !== 'not_found');
                    const hasCorrection = Boolean(selected && (
                      matched.length > 1 ||
                      selected.previewStatus === 'error' ||
                      selected.previewStatus === 'needs_mapping' ||
                      selected.previewStatus === 'needs_resolution' ||
                      selected.previewStatus === 'needs_review' ||
                      selected.previewStatus === 'idle'
                    ));
                    // IND-019: tras la carga diaria, todo fichero que no se
                    // publicó es «No publicado» (con su motivo).
                    const settled = Boolean(selected && compact && files.length > 0 &&
                      !loadingFolder && !confirmingBatch &&
                      !['loading', 'importing', 'ready', 'skipped'].includes(selected.previewStatus));
                    const dailyOutcome = compact && selected
                      ? getDailyOutcome(selected) ??
                        (settled ? { kind: 'error' as const, label: 'No publicado', color: 'error' as const } : null)
                      : null;
                    // 'Datos cargados' significa datos verificables en la fuente,
                    // NO que la última ejecución esté recordada tras recargar.
                    const completed = selected && (
                      selected.previewStatus === 'imported' ||
                      selected.previewStatus === 'imported_with_issues');
                    // IND-019: tras recargar, la tarjeta sale del resultado guardado.
                    const savedRunFile = compact && files.length === 0
                      // Una copia omitida («No cargado») no tapa al fichero real.
                      ? lastDailyRun?.files.find((file) => file.source_key === importer.key && file.status !== 'not_loaded') ??
                        lastDailyRun?.files.find((file) => file.source_key === importer.key) ?? null
                      : null;
                    const runFile = dailyOutcome && selected ? getDailyRunFile(selected) : savedRunFile;
                    const runLine = runFile
                      ? resultLine(runFile) ?? (runFile.status === 'error' || runFile.status === 'not_loaded' ? runFile.summary : null)
                      : null;
                    const notInFolder = compact && !selected && files.length > 0;
                    const displayStatus = matched.length > 1
                      ? 'Revisar archivos'
                      : runFile
                        ? DAILY_STATUS_LABELS[runFile.status]
                        : notInFolder
                          ? 'No cargado'
                      : completed
                        ? 'Datos cargados'
                        : selected
                          ? getPreviewStatusLabel(selected.previewStatus)
                          : sourceStatusesLoading
                            ? 'Comprobando...'
                            : sourceStatusesError
                              ? 'No se pudo consultar'
                              : hasPriorData
                                ? 'Datos cargados'
                                : 'Sin datos';
                    const displayColor = matched.length > 1
                      ? 'warning'
                      : runFile
                        ? DAILY_STATUS_COLORS[runFile.status]
                        : notInFolder
                          ? 'default'
                      : completed
                        ? 'success'
                        : selected
                          ? getPreviewStatusChipColor(selected.previewStatus)
                          : hasPriorData
                            ? 'success'
                            : 'default';

                    return (
                      <Paper
                        key={importer.key}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2.5,
                          minHeight: 138,
                          bgcolor: 'rgba(2,6,23,0.38)',
                          borderColor: 'rgba(148,163,184,0.16)',
                        }}
                      >
                        <Stack spacing={1.65} height="100%">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: 15 }}>
                              {importer.label}
                            </Typography>
                            <Chip
                              size="small"
                              label={displayStatus}
                              color={displayColor}
                              variant="outlined"
                              sx={{ mt: 0.8, maxWidth: '100%' }}
                            />
                          </Box>
                          {notInFolder ? (
                            <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
                              No estaba en la carpeta.
                            </Typography>
                          ) : savedRunFile ? (
                            <Box sx={{ pt: 1, borderTop: '1px solid rgba(148,163,184,0.14)' }}>
                              {savedRunFile.file_name ? (
                                <Typography sx={{ color: '#7dd3fc', fontSize: 11, overflowWrap: 'anywhere' }}>
                                  Seleccionado: {savedRunFile.file_name}
                                </Typography>
                              ) : null}
                              {runLine ? (
                                <Typography sx={{ mt: 0.4, color: savedRunFile.status === 'error' ? '#fbbf24' : '#cbd5e1', fontSize: 12 }}>
                                  {runLine}
                                </Typography>
                              ) : null}
                            </Box>
                          ) : !selected && !sourceStatusesLoading && !sourceStatusesError ? (
                            <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
                              {hasPriorData ? 'Hay información registrada en esta fuente.' : 'No se han encontrado datos.'}
                            </Typography>
                          ) : null}
                          {selected ? (
                            <Box sx={{ pt: 1, borderTop: '1px solid rgba(148,163,184,0.14)' }}>
                              <Typography sx={{ color: '#7dd3fc', fontSize: 11, overflowWrap: 'anywhere' }}>
                                Seleccionado: {selected.file.name}
                              </Typography>
                              {/* IND-019: solo la línea de resultado (o el motivo de
                                  «No publicado»); los avisos, en «Revisa tus ficheros». */}
                              {compact ? (
                                runLine ? (
                                  <Typography sx={{ mt: 0.4, color: runFile?.status === 'error' ? '#fbbf24' : '#cbd5e1', fontSize: 12 }}>
                                    {runLine}
                                  </Typography>
                                ) : null
                              ) : selected.confirmSummary ? (
                                <Typography sx={{ mt: 0.4, color: '#cbd5e1', fontSize: 12 }}>
                                  {selected.confirmSummary}
                                </Typography>
                              ) : hasCorrection ? (
                                <Typography sx={{ mt: 0.4, color: '#fbbf24', fontSize: 11 }}>
                                  {getStatusDetail(selected)}
                                </Typography>
                              ) : null}
                              {hasCorrection ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  sx={{ mt: 0.5, px: 0, textTransform: 'none' }}
                                  onClick={() => {
                                    setActiveFileId(selected.id);
                                    setShowAdvanced(true);
                                  }}
                                >
                                  Revisar incidencia
                                </Button>
                              ) : null}
                            </Box>
                          ) : null}
                        </Stack>
                      </Paper>
                    );
                  })}
              </Box>

              {files.some((item) => item.detectedType === 'unknown' || item.detectedType === 'excluded') ? (
                <Alert severity="warning" action={
                  <Button color="inherit" size="small" onClick={() => setShowAdvanced(true)}>
                    Revisar
                  </Button>
                }>
                  {files.filter((item) => item.detectedType === 'unknown' ||
                    item.detectedType === 'excluded').length} archivo(s) sin fuente asignada o excluidos.
                </Alert>
              ) : null}

              {duplicateReadyImporter ? (
                <Alert severity="warning">
                  Hay más de un archivo listo para una misma fuente. Revísalos antes de actualizar.
                </Alert>
              ) : null}

              {files.length > 0 ? (
                <Stack spacing={0.6}>
                  <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
                    {summary.importedTotalCount > 0
                      ? 'Carga terminada. El detalle de cada fuente aparece arriba.'
                      : `${summary.readyCount} fuentes listas · ${summary.requiresActionCount} por revisar`}
                  </Typography>
                  {summary.importedTotalCount === 0 && summary.requiresActionCount > 0 ? (
                    <Typography sx={{ color: '#fbbf24', fontSize: 12 }}>
                      No se ha iniciado la carga porque alguna fuente requiere revisión.
                    </Typography>
                  ) : null}
                </Stack>
              ) : (
                <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
                  Selecciona una carpeta para analizar y actualizar automáticamente las cuatro fuentes.
                </Typography>
              )}
            </>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      {compact && showAdvanced ? (
        <Button variant="outlined" sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          onClick={() => setShowAdvanced(false)}>
          Volver a actualización diaria
        </Button>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".xlsx,.xls,.csv"
        // @ts-expect-error webkitdirectory es soportado por navegador pero no está tipado en React
        webkitdirectory=""
        style={{ display: 'none' }}
        onChange={handleFolderSelected}
      />

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.7)',
          backdropFilter: 'blur(12px)',
          px: { xs: 2.5, md: 3.25 },
          py: { xs: 2.5, md: 3.25 },
        }}
      >
        <Stack spacing={2.25}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', lg: 'center' }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ color: '#f8fafc', fontWeight: 950, letterSpacing: '-0.8px' }}
              >
                Centro de importación masiva
              </Typography>
              <Typography sx={{ color: '#cbd5e1', mt: 1, maxWidth: 920, lineHeight: 1.7 }}>
                Selecciona una carpeta para analizar las fuentes y revisa sus estados.
                La publicación requiere confirmación explícita.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Button
                variant="outlined"
                onClick={() => void handleConfirmReadyBatch()}
                disabled={!canConfirmBatch || confirmingBatch || loadingFolder || !canImport}
              >
                {confirmingBatch ? 'Importando lote...' : 'Importar lote listo'}
              </Button>
              <Button
                variant="contained"
                onClick={handleOpenFolderPicker}
                disabled={loadingFolder || confirmingBatch || !canImport}
              >
                Seleccionar carpeta
              </Button>
            </Stack>
          </Stack>

          {(loadingFolder || confirmingBatch) && <LinearProgress />}

          <Alert severity="info">
            Esta vista utiliza Producción, Entradas, OF pendientes y Compras pendientes.
            La selección solo ejecuta la validación. Los archivos con errores
            bloqueantes requieren revisión y nunca se publican sin confirmar.
          </Alert>

          <Alert
            severity={batchStatusAlert.severity}
            action={
              batchStatusAlert.actionLabel ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void handleConfirmReadyBatch()}
                  disabled={!canConfirmBatch || confirmingBatch || loadingFolder || !canImport}
                >
                  {batchStatusAlert.actionLabel}
                </Button>
              ) : undefined
            }
          >
            <strong>{batchStatusAlert.title}:</strong> {batchStatusAlert.message}
          </Alert>

          {pageError ? <Alert severity="error">{pageError}</Alert> : null}
          {pageSuccess ? <Alert severity="success">{pageSuccess}</Alert> : null}
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            xl: 'repeat(6, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {[
          { label: 'Archivos detectados', value: summary.total, color: '#e2e8f0' },
          ...getImportCenterVisibleImporters().map((importer) => ({
            label: importer.label,
            value: summary.countByImporter[importer.key] ?? 0,
            color:
              importer.key === 'production'
                ? '#86efac'
                : importer.key === 'direct-inputs'
                  ? '#67e8f9'
                  : '#e2e8f0',
          })),
          { label: 'Requieren acción', value: summary.requiresActionCount, color: '#fbbf24' },
          { label: 'Listos para importar', value: summary.readyCount, color: '#86efac' },
          { label: 'Actualizados OK', value: summary.autoImportedOkCount, color: '#a78bfa' },
          {
            label: 'Actualizados con incidencias',
            value: summary.autoImportedWithIssuesCount,
            color: '#f59e0b',
          },
          { label: 'Fuera del lote', value: summary.outOfBatchCount, color: '#cbd5e1' },
        ].map((card) => (
          <Paper
            key={card.label}
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.18)',
              bgcolor: 'rgba(15,23,42,0.66)',
              backdropFilter: 'blur(10px)',
              p: 2.5,
            }}
          >
            <Stack spacing={0.75}>
              <Typography sx={{ color: '#94a3b8', fontSize: 13, fontWeight: 700 }}>
                {card.label}
              </Typography>
              <Typography sx={{ color: card.color, fontSize: 28, fontWeight: 950 }}>
                {card.value}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.35fr) minmax(360px, 0.95fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Stack spacing={3}>
          {[
            ...groupedFiles.importerGroups,
            groupedFiles.pendingReviewGroup,
            groupedFiles.outOfBatchGroup,
          ].map((group) => (
            <Paper
              key={group.title}
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(15,23,42,0.66)',
                backdropFilter: 'blur(10px)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid rgba(148,163,184,0.12)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Box>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: 20 }}>
                      {group.title}
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: 14, mt: 0.35 }}>
                      {group.helper}
                    </Typography>
                  </Box>

                  <Chip label={`${group.items.length} archivo(s)`} />
                </Stack>
              </Box>

              <Stack spacing={1.5} sx={{ p: 2 }}>
                {group.items.length === 0 ? (
                  <Typography sx={{ color: '#64748b', px: 0.5, py: 0.5 }}>
                    No hay archivos en este grupo.
                  </Typography>
                ) : (
                  group.items.map((item) => {
                    const selected = item.id === activeFileId;

                    return (
                      <Paper
                        key={item.id}
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          border: selected
                            ? '1px solid rgba(56,189,248,0.55)'
                            : '1px solid rgba(148,163,184,0.12)',
                          bgcolor: selected
                            ? 'rgba(14,116,144,0.14)'
                            : 'rgba(8,18,36,0.52)',
                          p: 2,
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Stack
                            direction={{ xs: 'column', lg: 'row' }}
                            spacing={1.5}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', lg: 'center' }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  color: '#f8fafc',
                                  fontWeight: 900,
                                  fontSize: 16,
                                  wordBreak: 'break-word',
                                }}
                              >
                                {item.file.name}
                              </Typography>
                              <Typography
                                sx={{
                                  color: '#64748b',
                                  fontSize: 12,
                                  mt: 0.35,
                                  wordBreak: 'break-all',
                                }}
                              >
                                {item.relativePath}
                              </Typography>
                            </Box>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                label={getDetectedTypeLabel(item.detectedType)}
                                color={getDetectedTypeChipColor(item.detectedType)}
                              />
                              <Chip
                                size="small"
                                label={getDetectionConfidenceLabel(item.detection.confidence)}
                                color={getDetectionConfidenceChipColor(item.detection.confidence)}
                                variant="outlined"
                              />
                              {item.autoReassignedByDetection ? (
                                <Chip
                                  size="small"
                                  label="Reasignado automáticamente"
                                  color="warning"
                                  variant="outlined"
                                />
                              ) : null}
                              {item.autoSelectedBestCandidate ? (
                                <Chip
                                  size="small"
                                  label="Mejor candidato seleccionado"
                                  color="primary"
                                  variant="outlined"
                                />
                              ) : null}
                              {item.learnedSuggestedImporter ? (
                                <Chip
                                  size="small"
                                  label={`${
                                    item.learnedSuggestionSource === 'persisted'
                                      ? 'Sugerencia recordada'
                                      : 'Sugerido por lote'
                                  }${
                                    item.learnedSuggestionScore !== null
                                      ? ` · score ${item.learnedSuggestionScore}`
                                      : ''
                                  }`}
                                  color={
                                    item.learnedSuggestionSource === 'persisted' &&
                                    (item.learnedSuggestionScore ?? 0) >=
                                      IMPORT_CENTER_PERSISTED_SUGGESTION_STRONG_SCORE
                                      ? 'success'
                                      : 'info'
                                  }
                                  variant="outlined"
                                />
                              ) : null}
                              <Chip
                                size="small"
                                label={getPreviewStatusLabel(item.previewStatus)}
                                color={getPreviewStatusChipColor(item.previewStatus)}
                              />
                            </Stack>
                          </Stack>

                          <Stack spacing={0.6}>
                            <Typography sx={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.6 }}>
                              {getStatusDetail(item)}
                            </Typography>
                            <Typography sx={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.5 }}>
                              {item.detection.decisionReason}
                            </Typography>
                          </Stack>

                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1.25}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                          >
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Columnas: ${item.detectedColumns.length}`}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Campos faltantes: ${item.missingRequiredFields.length}`}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Filas preview: ${item.previewRows.length}`}
                              />
                            </Stack>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Button
                                size="small"
                                variant={selected ? 'contained' : 'outlined'}
                                onClick={() => setActiveFileId(item.id)}
                              >
                                {selected ? 'Abierto' : 'Abrir'}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => void reanalyzeFile(item.id)}
                                disabled={
                                  item.previewStatus === 'loading' ||
                                  item.previewStatus === 'importing'
                                }
                              >
                                Reanalizar
                              </Button>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            position: { xl: 'sticky' },
            top: { xl: 24 },
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: '1px solid rgba(148,163,184,0.12)',
            }}
          >
            <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: 20 }}>
              Detalle del archivo
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: 14, mt: 0.35 }}>
              Selecciona un archivo del lote para revisar y operar sobre él.
            </Typography>
          </Box>

          {!activeFile ? (
            <Box sx={{ p: 2.5 }}>
              <Typography sx={{ color: '#64748b' }}>
                Aún no hay un archivo seleccionado.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2.25} sx={{ p: 2.5 }}>
              <Stack spacing={1.1}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: 18 }}>
                  {activeFile.file.name}
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: 12, wordBreak: 'break-all' }}>
                  {activeFile.relativePath}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={getDetectedTypeLabel(activeFile.detectedType)}
                  color={getDetectedTypeChipColor(activeFile.detectedType)}
                />
                <Chip
                  label={getDetectionConfidenceLabel(activeFile.detection.confidence)}
                  color={getDetectionConfidenceChipColor(activeFile.detection.confidence)}
                  variant="outlined"
                />
                {activeFile.autoReassignedByDetection ? (
                  <Chip
                    label="Reasignado automáticamente"
                    color="warning"
                    variant="outlined"
                  />
                ) : null}
                {activeFile.autoSelectedBestCandidate ? (
                  <Chip
                    label="Mejor candidato seleccionado"
                    color="primary"
                    variant="outlined"
                  />
                ) : null}
                {activeFile.learnedSuggestedImporter ? (
                  <Chip
                    label={`${
                      activeFile.learnedSuggestionSource === 'persisted'
                        ? 'Sugerencia recordada'
                        : 'Sugerido por lote'
                    }${
                      activeFile.learnedSuggestionScore !== null
                        ? ` · score ${activeFile.learnedSuggestionScore}`
                        : ''
                    }`}
                    color={
                      activeFile.learnedSuggestionSource === 'persisted' &&
                      (activeFile.learnedSuggestionScore ?? 0) >=
                        IMPORT_CENTER_PERSISTED_SUGGESTION_STRONG_SCORE
                        ? 'success'
                        : 'info'
                    }
                    variant="outlined"
                  />
                ) : null}
                <Chip
                  label={getPreviewStatusLabel(activeFile.previewStatus)}
                  color={getPreviewStatusChipColor(activeFile.previewStatus)}
                />
                {activeFile.confirmSummary ? (
                  <Chip label="Con resultado" color="success" variant="outlined" />
                ) : null}
              </Stack>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.12)',
                  bgcolor: 'rgba(8,18,36,0.5)',
                  p: 2,
                }}
              >
                <Stack spacing={1.25}>
                  <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                    Detección automática
                  </Typography>

                  <Stack spacing={0.5}>
                    <Typography sx={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.6 }}>
                      {activeFile.detection.decisionReason}
                    </Typography>
                    {activeFile.learnedSuggestedImporter ? (
                      <Typography sx={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.5 }}>
                        {activeFile.learnedSuggestionSource === 'persisted'
                          ? `Esta sugerencia recordada proviene de aprendizaje de lotes anteriores en este navegador. Cuando existe, el sistema prueba primero ese importador sugerido.${persistedSuggestionForActiveFile?.structuralSignature ? ' La coincidencia se ha podido reforzar con una firma estructural ligera del archivo.' : ''}`
                          : 'Esta sugerencia intra-lote se ha generado a partir de archivos similares ya validados en la misma carpeta, combinando similitud de nombre y compatibilidad ligera de estructura. Cuando existe, el sistema prueba primero ese importador sugerido.'}
                      </Typography>
                    ) : null}
                  </Stack>

                  {activeFile.detection.candidates.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {activeFile.detection.candidates.map((candidate) => (
                        <Chip
                          key={`${activeFile.id}-${candidate.importerKey}`}
                          size="small"
                          variant="outlined"
                          label={`${getDetectedTypeLabel(candidate.importerKey)} · score ${candidate.score}`}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                      No hay candidatos detectados automáticamente para este archivo.
                    </Typography>
                  )}
                </Stack>
              </Paper>

              <Alert severity="info">
                <strong>Siguiente acción:</strong> {getNextActionMessage(activeFile)}
              </Alert>
              {activeFile.detectedType === 'imported-manufacturing-orders' &&
              activeFile.previewStatus === 'ready' &&
              unresolvedProductRefs.length > 0 ? (
                <Alert severity="warning">
                  {unresolvedProductRefs.length} referencias de producto sin homologar.
                  No bloquean la publicación del snapshot de OF; se conserva
                  la referencia original para resolverla posteriormente.
                </Alert>
              ) : null}

              {!shouldShowExpandedDetailByDefault(activeFile) ? (
                <Alert
                  severity={
                    activeFile.previewStatus === 'imported_with_issues'
                      ? 'warning'
                      : 'success'
                  }
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() =>
                        setShowResolvedFileDetails((prev) => !prev)
                      }
                    >
                      {showResolvedFileDetails ? 'Ocultar detalle' : 'Mostrar detalle'}
                    </Button>
                  }
                >
                  Este archivo no requiere atención inmediata. Puedes trabajar en modo
                  compacto y abrir el detalle completo solo si necesitas revisarlo.
                </Alert>
              ) : null}

              <Stack spacing={1.1}>
                <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                  Tipo asignado
                </Typography>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={activeFile.detectedType}
                  onChange={(event) =>
                    void handleChangeDetectedType(
                      activeFile.id,
                      event.target.value as ImportCenterDetectedType,
                    )
                  }
                >
                  {getImportCenterSelectableDetectedTypes().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {activeFile.detectedType === 'production' ? (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.12)',
                    bgcolor: 'rgba(8,18,36,0.5)',
                    p: 2,
                  }}
                >
                  <Stack spacing={1.5}>
                    <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                      Configuración de Producción
                    </Typography>

                    {/* IND-002: en la carga diaria la producción es siempre
                        diaria y sustituye el rango de fechas del fichero. Las
                        demás opciones solo están en la carga individual. */}
                    <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
                      Producción diaria: se sustituyen los días que trae el
                      fichero. Para cargas mensuales, anuales o históricas usa
                      la carga individual de Producción.
                    </Typography>

                    {activeFile.productionConfig.sourceGranularity !== 'day' ? (
                      <Alert severity="warning">
                        Esta producción no tiene fecha diaria: los materiales que consume quedarán sin stock calculado hasta cargar producción diaria.
                      </Alert>
                    ) : null}

                    {activeFile.productionConfig.sourceGranularity !== 'day' ? (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Año declarado"
                          value={activeFile.productionConfig.declaredPeriodYear}
                          onChange={(event) =>
                            handleChangeProductionConfig(
                              'declaredPeriodYear',
                              event.target.value,
                            )
                          }
                        />
                        <TextField
                          size="small"
                          fullWidth
                          label="Mes declarado"
                          value={activeFile.productionConfig.declaredPeriodMonth}
                          onChange={(event) =>
                            handleChangeProductionConfig(
                              'declaredPeriodMonth',
                              event.target.value,
                            )
                          }
                        />
                      </Stack>
                    ) : null}

                    <Button
                      variant="outlined"
                      onClick={() => void reanalyzeFile(activeFile.id)}
                      disabled={
                        activeFile.previewStatus === 'loading' ||
                        activeFile.previewStatus === 'importing'
                      }
                    >
                      Actualizar preview de producción
                    </Button>
                  </Stack>
                </Paper>
              ) : null}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
                  gap: 1.25,
                }}
              >
                {[
                  { label: 'Columnas', value: activeFile.detectedColumns.length },
                  { label: 'Faltantes', value: activeFile.missingRequiredFields.length },
                  {
                    label: 'Preview rows',
                    value: activeFile.previewRows.length,
                  },
                  {
                    label: 'Homologaciones pendientes',
                    value:
                      activeFile.detectedType === 'production' ||
                      activeFile.detectedType ===
                        'imported-manufacturing-orders'
                        ? unresolvedProductRefs.length
                        : activeFile.detectedType ===
                            'direct-inputs'
                          ? unresolvedMaterialRefs.length
                          : 0,
                  },
                ].map((metric) => (
                  <Paper
                    key={metric.label}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(148,163,184,0.12)',
                      bgcolor: 'rgba(8,18,36,0.5)',
                      p: 1.5,
                    }}
                  >
                    <Typography sx={{ color: '#64748b', fontSize: 12, fontWeight: 700 }}>
                      {metric.label}
                    </Typography>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: 22 }}>
                      {metric.value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              {(
                activeFile.previewError ||
                activeFile.missingRequiredFields.length > 0 ||
                (activeFile.detectedType === 'purchase-orders' &&
                  activePurchaseOrdersSummary &&
                  activePurchaseOrdersSummary.allIssues.length > 0) ||
                (
                  activeFile.detectedType === 'direct-inputs' &&
                  activeDirectInputsSummary &&
                  activeDirectInputsSummary.nonBlockingIssues.length > 0
                )
              ) ? (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.12)',
                    bgcolor: 'rgba(8,18,36,0.5)',
                    p: 2,
                  }}
                >
                  <Stack spacing={1.25}>
                    <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                      Incidencias y revisión
                    </Typography>

                    {activeFile.previewError ? (
                      <Alert severity="error">{activeFile.previewError}</Alert>
                    ) : null}

                    {activeFile.missingRequiredFields.length > 0 ? (
                      <Alert severity="warning">
                        Campos obligatorios sin mapear: {activeFile.missingRequiredFields.join(', ')}
                      </Alert>
                    ) : null}

                    {activeFile.detectedType === 'purchase-orders' &&
                    activePurchaseOrdersSummary &&
                    activePurchaseOrdersSummary.allIssues.length > 0 ? (
                      <Alert severity={activePurchaseOrdersSummary.canContinue ? 'info' : 'warning'}>
                        <Stack spacing={0.5}>
                          {activePurchaseOrdersSummary.allIssues.map((issue) => (
                            <Typography key={issue.kind} sx={{ fontSize: 14 }}>
                              {issue.message}
                            </Typography>
                          ))}
                        </Stack>
                      </Alert>
                    ) : null}

                    {activeFile.detectedType === 'purchase-orders' &&
                    activeFile.purchaseOrdersPreview?.row_errors.length ? (
                      <Alert severity="error">
                        {activeFile.purchaseOrdersPreview.row_errors.slice(0, 5).map((row, index) => (
                          <Typography key={index} sx={{ fontSize: 13 }}>
                            Fila {String(row.source_row_number ?? '?')}: {String(row.message ?? row.error ?? 'Registro inválido')}
                          </Typography>
                        ))}
                      </Alert>
                    ) : null}

                    {activeFile.detectedType === 'direct-inputs' &&
                    activeDirectInputsSummary &&
                    activeDirectInputsSummary.nonBlockingIssues.length > 0 ? (
                      <Alert severity="info">
                        <Stack spacing={0.5}>
                          {activeDirectInputsSummary.nonBlockingIssues.map((issue) => (
                            <Typography
                              key={`${issue.kind}-${issue.count}`}
                              sx={{ fontSize: 14 }}
                            >
                              {issue.message}
                            </Typography>
                          ))}
                        </Stack>
                      </Alert>
                    ) : null}
                  </Stack>
                </Paper>
              ) : null}

              {shouldShowExpandedActiveDetail ? (
                <>
                  <Divider sx={{ borderColor: 'rgba(148,163,184,0.12)' }} />

                  <Stack spacing={1.1}>
                    <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                      Mapeo de columnas
                    </Typography>

                {Object.keys(activeFile.mapping).length === 0 ? (
                  <Typography sx={{ color: '#64748b' }}>
                    El sistema aún no ha generado un mapeo para este archivo.
                  </Typography>
                ) : (
                  <Stack spacing={1.25}>
                    {sortedMappingEntries.map(([field, value]) => (
                      <Stack
                        key={field}
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.25}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                      >
                        <Box sx={{ width: { xs: '100%', md: 180 }, flexShrink: 0 }}>
                          <Typography sx={{ color: '#cbd5e1', fontWeight: 700 }}>
                            {formatFieldLabel(field)}
                          </Typography>
                        </Box>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={value ?? ''}
                          onChange={(event) =>
                            void handleChangeMapping(field, event.target.value)
                          }
                        >
                          <MenuItem value="">Sin asignar</MenuItem>
                          {activeFile.detectedColumns.map((column) => (
                            <MenuItem key={column} value={column}>
                              {column}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>

              {(activeFile.detectedType === 'production' ||
                activeFile.detectedType ===
                  'imported-manufacturing-orders') &&
              sortedDetectedProductRefs.length > 0 ? (
                <>
                  <Divider sx={{ borderColor: 'rgba(148,163,184,0.12)' }} />
                  <Stack spacing={1.1}>
                    <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                      Homologación de productos
                    </Typography>

                    {loadingReferenceData ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>
                          Cargando catálogo de productos...
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack spacing={1.25}>
                        {sortedDetectedProductRefs.map((ref) => {
                          const resolvedValue =
                            activeFile.productResolution[ref.source_value] ??
                            ref.resolved_product_id ??
                            '';

                          const isResolved = Boolean(resolvedValue);

                          return (
                            <Stack
                              key={ref.normalized_value}
                              spacing={0.8}
                              sx={{
                                p: 1.5,
                                borderRadius: 2.5,
                                border: '1px solid rgba(148,163,184,0.12)',
                                bgcolor: 'rgba(8,18,36,0.48)',
                              }}
                            >
                              <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                              >
                                <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                                  {ref.source_value}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={isResolved ? 'Resuelto' : 'Pendiente'}
                                  color={isResolved ? 'success' : 'warning'}
                                />
                              </Stack>

                              <Typography sx={{ color: '#64748b', fontSize: 12 }}>
                                {ref.row_count} fila(s)
                              </Typography>

                              <TextField
                                select
                                size="small"
                                fullWidth
                                value={resolvedValue}
                                onChange={(event) =>
                                  void handleChangeProductResolution(
                                    ref.source_value,
                                    event.target.value,
                                  )
                                }
                              >
                                <MenuItem value="">Sin homologar</MenuItem>
                                {activeProducts.map((product) => (
                                  <MenuItem key={product.id} value={product.id}>
                                    {product.code} · {product.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </Stack>
                </>
              ) : null}

              {activeFile.detectedType === 'direct-inputs' &&
              sortedDetectedMaterialRefs.length > 0 ? (
                <>
                  <Divider sx={{ borderColor: 'rgba(148,163,184,0.12)' }} />
                  <Stack spacing={1.1}>
                    <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                      Homologación de materiales
                    </Typography>

                    {loadingReferenceData ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>
                          Cargando catálogo de materiales...
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack spacing={1.25}>
                        {sortedDetectedMaterialRefs.map((ref) => {
                          const resolvedValue =
                            activeFile.materialResolution[ref.source_value] ??
                            ref.resolved_material_id ??
                            '';

                          const isResolved = Boolean(resolvedValue);

                          return (
                            <Stack
                              key={ref.normalized_value}
                              spacing={0.8}
                              sx={{
                                p: 1.5,
                                borderRadius: 2.5,
                                border: '1px solid rgba(148,163,184,0.12)',
                                bgcolor: 'rgba(8,18,36,0.48)',
                              }}
                            >
                              <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                              >
                                <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                                  {ref.source_value}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={isResolved ? 'Resuelto' : 'Pendiente'}
                                  color={isResolved ? 'success' : 'warning'}
                                />
                              </Stack>

                              <Typography sx={{ color: '#64748b', fontSize: 12 }}>
                                {ref.row_count} fila(s)
                              </Typography>

                              <TextField
                                select
                                size="small"
                                fullWidth
                                value={resolvedValue}
                                onChange={(event) =>
                                  void handleChangeMaterialResolution(
                                    ref.source_value,
                                    event.target.value,
                                  )
                                }
                              >
                                <MenuItem value="">Sin homologar</MenuItem>
                                {activeMaterials.map((material) => (
                                  <MenuItem key={material.id} value={material.id}>
                                    {material.code} · {material.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </Stack>
                </>
              ) : null}

              <Divider sx={{ borderColor: 'rgba(148,163,184,0.12)' }} />

              <Stack spacing={1.1}>
                <Typography sx={{ color: '#cbd5e1', fontWeight: 800 }}>
                  Verificación final del preview
                </Typography>

                <Typography sx={{ color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
                  Usa esta muestra solo como comprobación final de que el archivo leído encaja
                  con lo esperado tras mapear, configurar y homologar.
                </Typography>

                {activeFile.previewRows.length === 0 ? (
                  <Typography sx={{ color: '#64748b' }}>
                    No hay filas de preview disponibles.
                  </Typography>
                ) : (
                  <Stack spacing={1.25}>
                    {activeFile.previewRows.slice(0, 3).map((row, index) => (
                      <Paper
                        key={`${activeFile.id}-preview-row-${index}`}
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          border: '1px solid rgba(148,163,184,0.10)',
                          bgcolor: 'rgba(8,18,36,0.38)',
                          p: 1.5,
                        }}
                      >
                        <Stack spacing={0.7}>
                          <Typography sx={{ color: '#64748b', fontSize: 12, fontWeight: 700 }}>
                            Fila {index + 1}
                          </Typography>
                          {Object.entries(row).map(([key, value]) => (
                            <Stack
                              key={key}
                              direction="row"
                              spacing={1}
                              justifyContent="space-between"
                            >
                              <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
                                {formatFieldLabel(key)}
                              </Typography>
                              <Typography
                                sx={{
                                  color: '#e2e8f0',
                                  fontSize: 13,
                                  textAlign: 'right',
                                  maxWidth: '65%',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {formatPreviewValue(value)}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Stack>

                </>
              ) : null}

              {activeFile.confirmSummary ? (
                <Alert
                  severity={
                    activeFile.previewStatus === 'imported_with_issues'
                      ? 'warning'
                      : 'success'
                  }
                >
                  {activeFile.confirmSummary}
                </Alert>
              ) : null}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <Button
                  variant="outlined"
                  onClick={() => void reanalyzeFile(activeFile.id)}
                  disabled={
                    activeFile.previewStatus === 'loading' ||
                    activeFile.previewStatus === 'importing'
                  }
                >
                  Reanalizar archivo
                </Button>

                <Button
                  variant="contained"
                  onClick={handleConfirmActiveFile}
                  disabled={!canConfirmActiveFile}
                >
                  Confirmar archivo
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Box>
    </Stack>
  );
}